import { useEffect, useState } from 'react';
import {
  User,
  Bell,
  MapPin,
  CreditCard,
  Moon,
  Globe,
  Shield,
  HelpCircle,
  LogOut,
  ChevronRight,
  Mail,
  Phone,
  Calendar,
  Settings,
  Smartphone,
  Lock,
  Eye,
  EyeOff,
  Trash2,
  Edit,
  Check,
  X,
  Info,
  Star,
  Heart,
  Clock,
  FileText,
  AlertCircle,
  Languages,
  DollarSign,
  Utensils,
  Volume2,
  VolumeX,
  Download,
  Share2,
  MessageSquare,
  Zap,
  Gift,
  Home,
  Briefcase,
} from 'lucide-react';
import type { UserType } from '../../App';
import { api } from '../../lib/api';
import { useLocale } from '../../lib/locale';
import { useTheme } from '../../contexts/ThemeContext';
import { MapAddressSelector } from './MapAddressSelector';
import { ThemeModeToggle } from './ThemeModeToggle';

interface CustomerSettingsProps {
  user: UserType;
  onClose: () => void;
  onLogout: () => void;
  initialSection?: SettingsSection;
}

type SettingsSection =
  | 'account'
  | 'appearance'
  | 'notifications'
  | 'privacy'
  | 'language'
  | 'payment'
  | 'offers'
  | 'addresses'
  | 'preferences'
  | 'help'
  | 'about';

interface NotificationSettings {
  orderUpdates: boolean;
  offers: boolean;
  newRestaurants: boolean;
  email: boolean;
  sms: boolean;
  push: boolean;
  sound: boolean;
}

interface PrivacySettings {
  shareData: boolean;
  locationAccess: boolean;
  activityTracking: boolean;
  personalizedAds: boolean;
}

interface OrderPreferences {
  contactlessDelivery: boolean;
  leaveAtDoor: boolean;
  dietaryPreference: 'all' | 'veg' | 'non-veg' | 'jain' | 'vegan';
  spiceLevel: 'mild' | 'medium' | 'hot';
  defaultTip: number;
}

interface SavedAddress {
  id: string;
  type: 'Home' | 'Work' | 'Other';
  address: string;
  city: string;
  isPrimary: boolean;
  lat?: number;
  lng?: number;
}

interface PaymentMethodOption {
  id: string;
  code: string;
  name: string;
  type: string;
  metadata?: Record<string, any>;
}

type CardBrand = 'Visa' | 'Mastercard' | 'RuPay' | 'Amex' | 'Discover' | 'Card';

interface SavedCard {
  id: string;
  brand: CardBrand;
  last4: string;
  expiry: string;
  isDefault: boolean;
  holderName?: string;
  nickname?: string;
  billingAddress?: string;
  token: string;
  createdAt: string;
}

interface MapAddressDetails {
  formattedAddress: string;
  street?: string;
  area?: string;
  city?: string;
  state?: string;
  postalCode?: string;
  country?: string;
  lat: number;
  lng: number;
  landmark?: string;
  houseNumber?: string;
  floor?: string;
  addressType?: 'Home' | 'Work' | 'Other';
  addressLabel?: string;
}

type WalletProviderId = 'phonepe' | 'gpay' | 'paytm';

interface WalletTransaction {
  id: string;
  orderId: string;
  amount: number;
  provider: WalletProviderId;
  createdAt: string;
}

interface WalletLinkState {
  linked: boolean;
  mobile?: string;
  linkedAt?: string;
  isDefault?: boolean;
  balance?: number | null;
  transactions?: WalletTransaction[];
  offers?: string[];
  deviceVerified?: boolean;
  pinRequired?: boolean;
  autoPay?: boolean;
}

const FALLBACK_LANGUAGES = [
  { code: 'EN', name: 'English' },
  { code: 'HI', name: 'Hindi' },
  { code: 'TA', name: 'Tamil' },
  { code: 'TE', name: 'Telugu' },
  { code: 'KN', name: 'Kannada' },
  { code: 'ML', name: 'Malayalam' },
  { code: 'BN', name: 'Bengali' },
  { code: 'MR', name: 'Marathi' },
];

const FALLBACK_PAYMENT_METHODS: PaymentMethodOption[] = [
  { id: 'card', code: 'CARD', name: 'Card', type: 'card' },
  { id: 'upi', code: 'UPI', name: 'UPI', type: 'upi' },
  { id: 'wallet', code: 'WALLET', name: 'Wallet', type: 'wallet' },
  { id: 'netbanking', code: 'NETBANKING', name: 'Net Banking', type: 'netbanking' },
  { id: 'cod', code: 'COD', name: 'Cash on Delivery', type: 'cash' },
];

const FALLBACK_CURRENCIES = [
  { code: 'INR', name: 'Indian Rupee', symbol: '₹' },
  { code: 'USD', name: 'US Dollar', symbol: '$' },
  { code: 'EUR', name: 'Euro', symbol: '€' },
  { code: 'GBP', name: 'British Pound', symbol: '£' },
];
const WALLET_PROVIDERS: Array<{
  id: WalletProviderId;
  name: string;
  description: string;
  accent: string;
}> = [
  { id: 'phonepe', name: 'PhonePe', description: 'Fast UPI wallet', accent: 'from-purple-500 to-pink-500' },
  { id: 'gpay', name: 'Google Pay', description: 'Google UPI wallet', accent: 'from-blue-500 to-blue-700' },
  { id: 'paytm', name: 'Paytm', description: 'Popular wallet', accent: 'from-indigo-500 to-purple-600' },
];

export function CustomerSettings({ user, onClose, onLogout, initialSection = 'account' }: CustomerSettingsProps) {
  const { formatMoney } = useLocale();
  const { themeMode } = useTheme();
  const CARD_STORAGE_KEY = `customer_cards_${user?.id || user?.email || 'guest'}`;
  const PAYMENT_LINKS_KEY = `customer_payment_links_${user?.id || user?.email || 'guest'}`;
  const [activeSection, setActiveSection] = useState<SettingsSection>(initialSection);
  const [language, setLanguage] = useState(() => {
    return (localStorage.getItem('language') || 'EN').toUpperCase();
  });
  const [currency, setCurrency] = useState(() => {
    return (localStorage.getItem('currency') || 'INR').toUpperCase();
  });
  const [pendingLanguage, setPendingLanguage] = useState(() => (localStorage.getItem('language') || 'EN').toUpperCase());
  const [pendingCurrency, setPendingCurrency] = useState(() => (localStorage.getItem('currency') || 'INR').toUpperCase());
  const [languageOptions, setLanguageOptions] = useState<Array<{ code: string; name: string; nativeName?: string; flag?: string }>>(FALLBACK_LANGUAGES);
  const [currencyOptions, setCurrencyOptions] = useState<Array<{ code: string; name: string; symbol: string }>>(FALLBACK_CURRENCIES);
  const [masterLoadError, setMasterLoadError] = useState<string | null>(null);
  const [paymentMethods, setPaymentMethods] = useState<PaymentMethodOption[]>(FALLBACK_PAYMENT_METHODS);
  const [paymentLoadError, setPaymentLoadError] = useState<string | null>(null);
  const [linkedPayments, setLinkedPayments] = useState<Record<WalletProviderId, WalletLinkState>>(
    {} as Record<WalletProviderId, WalletLinkState>
  );
  const [savedCardsState, setSavedCardsState] = useState<SavedCard[]>([]);
  const [showAddCardModal, setShowAddCardModal] = useState(false);
  const [cardForm, setCardForm] = useState({
    number: '',
    name: '',
    expiry: '',
    cvv: '',
    nickname: '',
    billingAddress: '',
    isDefault: false,
    saveCard: true,
  });
  const [cardErrors, setCardErrors] = useState<Record<string, string>>({});
  const [editCardForm, setEditCardForm] = useState({
    nickname: '',
    expiry: '',
    billingAddress: '',
    isDefault: false,
  });
  const [editErrors, setEditErrors] = useState<Record<string, string>>({});
  const [editingCard, setEditingCard] = useState<SavedCard | null>(null);
  const [cardSuccessMessage, setCardSuccessMessage] = useState<string | null>(null);
  const [walletModalProvider, setWalletModalProvider] = useState<WalletProviderId | null>(null);
  const [walletModalMode, setWalletModalMode] = useState<'link' | 'otp' | 'manage'>('link');
  const [walletMobile, setWalletMobile] = useState('');
  const [walletOtp, setWalletOtp] = useState('');
  const [walletPin, setWalletPin] = useState('');
  const [walletDeviceVerified, setWalletDeviceVerified] = useState(true);
  const [walletPinRequired, setWalletPinRequired] = useState(false);
  const [walletLinkError, setWalletLinkError] = useState('');
  const [walletOtpMessage, setWalletOtpMessage] = useState('');
  const [saveMessage, setSaveMessage] = useState<string | null>(null);
  
  const [notifications, setNotifications] = useState<NotificationSettings>({
    orderUpdates: true,
    offers: true,
    newRestaurants: false,
    email: true,
    sms: false,
    push: true,
    sound: true,
  });

  const [privacy, setPrivacy] = useState<PrivacySettings>({
    shareData: false,
    locationAccess: true,
    activityTracking: true,
    personalizedAds: false,
  });

  const [preferences, setPreferences] = useState<OrderPreferences>({
    contactlessDelivery: true,
    leaveAtDoor: false,
    dietaryPreference: 'all',
    spiceLevel: 'medium',
    defaultTip: 0,
  });

  const [isEditingProfile, setIsEditingProfile] = useState(false);
  const [profileData, setProfileData] = useState({
    name: user?.name ?? '',
    email: user?.email ?? '',
    phone: user?.phone ?? '',
    birthday: '15/08/1995',
  });

  const menuItems = [
    { id: 'account', icon: User, label: 'Account Settings', description: 'Manage your profile' },
    { id: 'appearance', icon: Moon, label: 'Appearance', description: 'Theme & display' },
    { id: 'notifications', icon: Bell, label: 'Notifications', description: 'Alerts & updates' },
    { id: 'privacy', icon: Shield, label: 'Privacy & Security', description: 'Control your data' },
    { id: 'language', icon: Globe, label: 'Language & Region', description: 'Language, currency' },
    { id: 'payment', icon: CreditCard, label: 'Payment Methods', description: 'Cards & wallets' },
    { id: 'offers', icon: Gift, label: 'Offers & Rewards', description: 'Coupons & points' },
    { id: 'addresses', icon: MapPin, label: 'Saved Addresses', description: 'Delivery locations' },
    { id: 'preferences', icon: Utensils, label: 'Order Preferences', description: 'Dietary & delivery' },
    { id: 'help', icon: HelpCircle, label: 'Help & Support', description: 'FAQs & contact' },
    { id: 'about', icon: Info, label: 'About', description: 'App info & legal' },
  ];


  const savedCards = savedCardsState;
  const activeWallet = walletModalProvider
    ? WALLET_PROVIDERS.find((wallet) => wallet.id === walletModalProvider) || null
    : null;
  const activeWalletState = walletModalProvider ? linkedPayments[walletModalProvider] : null;
  const [savedAddresses, setSavedAddresses] = useState<SavedAddress[]>([]);
  const [mapInitialLocation, setMapInitialLocation] = useState<{ lat: number; lng: number } | undefined>(() => {
    const lat = Number(localStorage.getItem('customer_latitude'));
    const lng = Number(localStorage.getItem('customer_longitude'));
    if (Number.isFinite(lat) && Number.isFinite(lng)) {
      return { lat, lng };
    }
    return undefined;
  });
  const [showMapSelector, setShowMapSelector] = useState(false);
  const [showChangePasswordModal, setShowChangePasswordModal] = useState(false);
  const [changePasswordForm, setChangePasswordForm] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: '',
    showCurrent: false,
    showNew: false,
    showConfirm: false,
  });
  const [changePasswordError, setChangePasswordError] = useState('');
  const [changePasswordSuccess, setChangePasswordSuccess] = useState('');
  const [changePasswordLoading, setChangePasswordLoading] = useState(false);
  const [passwordUpdateBanner, setPasswordUpdateBanner] = useState<string | null>(null);
  const [rewardPoints, setRewardPoints] = useState(1250);
  const [offersMessage, setOffersMessage] = useState<string | null>(null);

  const normalizeKey = (value: string) =>
    String(value || '')
      .toUpperCase()
      .replace(/[^A-Z]/g, '');

  const parseMetadata = (value: any) => {
    if (!value) return {};
    if (typeof value === 'string') {
      try {
        return JSON.parse(value);
      } catch {
        return {};
      }
    }
    if (typeof value === 'object') return value;
    return {};
  };

  const normalizePaymentType = (value: string) => {
    const key = String(value || '').toLowerCase();
    if (key.includes('card')) return 'card';
    if (key.includes('wallet')) return 'wallet';
    if (key.includes('upi')) return 'upi';
    if (key.includes('net')) return 'netbanking';
    if (key.includes('cash') || key.includes('cod')) return 'cash';
    return 'other';
  };

  const detectCardBrand = (number: string): CardBrand => {
    const clean = String(number || '').replace(/\D/g, '');
    if (/^4/.test(clean)) return 'Visa';
    if (/^(5[1-5]|2[2-7])/.test(clean)) return 'Mastercard';
    if (/^3[47]/.test(clean)) return 'Amex';
    if (/^6/.test(clean)) return 'RuPay';
    if (/^6011|^65|^64[4-9]/.test(clean)) return 'Discover';
    return 'Card';
  };

  const formatCardNumber = (value: string) => {
    const cleaned = String(value || '').replace(/\D/g, '').slice(0, 19);
    const parts = cleaned.match(/.{1,4}/g);
    return parts ? parts.join(' ') : cleaned;
  };

  const luhnCheck = (value: string) => {
    const digits = String(value || '').replace(/\D/g, '');
    if (!digits) return false;
    let sum = 0;
    let shouldDouble = false;
    for (let i = digits.length - 1; i >= 0; i -= 1) {
      let digit = Number(digits[i]);
      if (shouldDouble) {
        digit *= 2;
        if (digit > 9) digit -= 9;
      }
      sum += digit;
      shouldDouble = !shouldDouble;
    }
    return sum % 10 === 0;
  };

  const isExpiryValid = (value: string) => {
    if (!/^(0[1-9]|1[0-2])\/\d{2}$/.test(value)) return false;
    const [monthStr, yearStr] = value.split('/');
    const month = Number(monthStr);
    const year = Number(yearStr);
    if (!month || Number.isNaN(year)) return false;
    const now = new Date();
    const currentMonth = now.getMonth() + 1;
    const currentYear = now.getFullYear() % 100;
    if (year < currentYear) return false;
    if (year === currentYear && month < currentMonth) return false;
    return true;
  };

  const getBrandStyles = (brand: CardBrand) => {
    switch (brand) {
      case 'Visa':
        return { label: 'VISA', bg: 'bg-blue-100', text: 'text-blue-700' };
      case 'Mastercard':
        return { label: 'MC', bg: 'bg-orange-100', text: 'text-orange-700' };
      case 'RuPay':
        return { label: 'RuPay', bg: 'bg-green-100', text: 'text-green-700' };
      case 'Amex':
        return { label: 'Amex', bg: 'bg-teal-100', text: 'text-teal-700' };
      case 'Discover':
        return { label: 'Discover', bg: 'bg-yellow-100', text: 'text-yellow-700' };
      default:
        return { label: 'Card', bg: 'bg-gray-100', text: 'text-gray-600' };
    }
  };

  const maskCardNumber = (last4: string) => `**** **** **** ${last4}`;

  const tokenizeCard = () => `tok_${Date.now()}_${Math.random().toString(36).slice(2, 10)}`;

  const validateCardNumber = (value: string) => {
    const clean = String(value || '').replace(/\D/g, '');
    if (!clean) return '';
    const brand = detectCardBrand(clean);
    const isLengthValid =
      (brand === 'Amex' && clean.length === 15) ||
      (brand !== 'Amex' && clean.length === 16);
    if (!/^\d+$/.test(clean) || !isLengthValid || !luhnCheck(clean)) {
      return 'Invalid Card Number';
    }
    return '';
  };

  const validateExpiry = (value: string) => {
    if (!value) return '';
    if (!/^(0[1-9]|1[0-2])\/\d{2}$/.test(value)) return 'Expiry must be in MM/YY format.';
    if (!isExpiryValid(value)) return 'Card Expired';
    return '';
  };

  const validateCvv = (value: string, brand: CardBrand) => {
    if (!value) return '';
    if (!(brand === 'Amex' ? /^\d{4}$/ : /^\d{3}$/).test(value)) return 'Invalid CVV';
    return '';
  };

  const handleEditAddress = (address: SavedAddress) => {
    const updatedAddress = window.prompt('Edit address', address.address);
    if (updatedAddress === null) return;

    const updatedCity = window.prompt('Edit city', address.city);
    if (updatedCity === null) return;

    setSavedAddresses((prev) =>
      prev.map((item) =>
        item.id === address.id
          ? {
              ...item,
              address: updatedAddress.trim() || address.address,
              city: updatedCity.trim() || address.city,
            }
          : item
      )
    );
  };

  const handleDeleteAddress = (addressId: string) => {
    const target = savedAddresses.find((item) => item.id === addressId);
    if (!target) return;

    const confirmed = window.confirm(`Delete ${target.type} address?`);
    if (!confirmed) return;

    let next = savedAddresses.filter((item) => item.id !== addressId);
    if (target.isPrimary && next.length > 0 && !next.some((item) => item.isPrimary)) {
      next = next.map((item, index) => (index === 0 ? { ...item, isPrimary: true } : item));
    }
    setSavedAddresses(next);
  };

  const loadSavedAddresses = async () => {
    const hasToken = Boolean(localStorage.getItem('auth_token') || localStorage.getItem('token'));
    if (!hasToken) {
      setSavedAddresses([]);
      return;
    }
    try {
      const response = await api.users.getSavedLocations();
      const rows = Array.isArray((response as any)?.data) ? (response as any).data : [];
      const mapped: SavedAddress[] = rows.map((item: any) => {
        const rawType = String(item.addressType || 'HOME').toUpperCase();
        const type: SavedAddress['type'] =
          rawType === 'OFFICE' ? 'Work' : rawType === 'OTHER' ? 'Other' : 'Home';
        return {
          id: item.id,
          type,
          address: item.fullAddress || item.address || '',
          city: item.areaLocality || item.city || '',
          isPrimary: Boolean(item.isDefault),
          lat: typeof item.latitude === 'number' ? item.latitude : undefined,
          lng: typeof item.longitude === 'number' ? item.longitude : undefined,
        };
      });
      setSavedAddresses(mapped);
    } catch {
      // Keep UI usable if API fails.
      setSavedAddresses((prev) => (prev.length ? prev : []));
    }
  };
  const handleMapAddressSelect = (details: MapAddressDetails) => {
    const type = details.addressType || 'Other';
    const address =
      details.formattedAddress ||
      [details.street, details.area, details.city, details.state].filter(Boolean).join(', ');
    const city = details.city || details.state || details.country || 'Unknown';

    const newAddress: SavedAddress = {
      id: `addr-${Date.now()}`,
      type,
      address,
      city,
      isPrimary: !savedAddresses.some((item) => item.isPrimary),
      lat: details.lat,
      lng: details.lng,
    };

    setSavedAddresses((prev) => {
      const next = newAddress.isPrimary ? prev.map((item) => ({ ...item, isPrimary: false })) : prev;
      return [newAddress, ...next];
    });
    localStorage.setItem('customer_latitude', String(details.lat));
    localStorage.setItem('customer_longitude', String(details.lng));
    localStorage.setItem('customer_location_updated_at', String(Date.now()));
    setMapInitialLocation({ lat: details.lat, lng: details.lng });
    setShowMapSelector(false);
    void loadSavedAddresses();
  };

  useEffect(() => {
    let mounted = true;

    const resolveMatchingOption = <T extends { code: string; name: string }>(
      options: T[],
      storedCode: string
    ) => {
      const key = normalizeKey(storedCode);
      if (!key) return options[0];
      return (
        options.find((opt) => normalizeKey(opt.code) === key) ||
        options.find((opt) => normalizeKey(opt.name) === key) ||
        options.find((opt) => normalizeKey(opt.code).startsWith(key) || key.startsWith(normalizeKey(opt.code))) ||
        options[0]
      );
    };

    const loadMasterData = async () => {
      try {
        const [languagesRes, currenciesRes] = await Promise.allSettled([
          api.masterData.languages.getAll(),
          api.masterData.currencies.getAll(),
        ]);

        const rawLanguages =
          languagesRes.status === 'fulfilled' && Array.isArray((languagesRes.value as any)?.data)
            ? (languagesRes.value as any).data
            : [];
        const rawCurrencies =
          currenciesRes.status === 'fulfilled' && Array.isArray((currenciesRes.value as any)?.data)
            ? (currenciesRes.value as any).data
            : [];

        const mappedLanguages = rawLanguages
          .map((row: any) => {
            const meta = parseMetadata(row?.metadata);
            return {
              code: String(row?.code || '').toUpperCase(),
              name: String(row?.name || ''),
              nativeName: meta?.nativeName || meta?.native || undefined,
              flag: meta?.flagEmoji || meta?.flag || undefined,
            };
          })
          .filter((row: any) => row.code && row.name);

        const mappedCurrencies = rawCurrencies
          .map((row: any) => {
            const meta = parseMetadata(row?.metadata);
            const symbol = row?.symbol || meta?.symbol || '';
            return {
              code: String(row?.code || '').toUpperCase(),
              name: String(row?.name || ''),
              symbol: String(symbol || '').trim(),
            };
          })
          .filter((row: any) => row.code && row.name);

        if (!mounted) return;
        const nextLanguages = mappedLanguages.length ? mappedLanguages : FALLBACK_LANGUAGES;
        const nextCurrencies = mappedCurrencies.length ? mappedCurrencies : FALLBACK_CURRENCIES;
        setLanguageOptions(nextLanguages);
        setCurrencyOptions(nextCurrencies);
        const matchedLanguage = resolveMatchingOption(nextLanguages, language);
        const matchedCurrency = resolveMatchingOption(nextCurrencies, currency);
        if (matchedLanguage) {
          setLanguage(String(matchedLanguage.code).toUpperCase());
          setPendingLanguage(String(matchedLanguage.code).toUpperCase());
        }
        if (matchedCurrency) {
          setCurrency(String(matchedCurrency.code).toUpperCase());
          setPendingCurrency(String(matchedCurrency.code).toUpperCase());
        }
        setMasterLoadError(null);
      } catch (error: any) {
        if (!mounted) return;
        setLanguageOptions(FALLBACK_LANGUAGES);
        setCurrencyOptions(FALLBACK_CURRENCIES);
        setMasterLoadError(error?.message || 'Unable to load master data');
      }
    };

    void loadMasterData();
    return () => {
      mounted = false;
    };
  }, []);

  const setLanguageSelection = (next: { code: string; name: string }) => {
    const normalized = String(next.code || '').toUpperCase();
    setLanguage(normalized);
    localStorage.setItem('language', normalized);
    localStorage.setItem('language_name', next.name);
    document.documentElement.lang = normalized.toLowerCase();
    window.dispatchEvent(new CustomEvent('languageChange', { detail: { code: normalized, name: next.name } }));
  };

  const setCurrencySelection = (next: { code: string; name: string; symbol: string }) => {
    const normalized = String(next.code || '').toUpperCase();
    setCurrency(normalized);
    localStorage.setItem('currency', normalized);
    localStorage.setItem('currency_name', next.name);
    if (next.symbol) {
      localStorage.setItem('currency_symbol', next.symbol);
    }
    window.dispatchEvent(
      new CustomEvent('currencyChange', { detail: { code: normalized, name: next.name, symbol: next.symbol } })
    );
  };

  useEffect(() => {
    if (language) {
      document.documentElement.lang = String(language).toLowerCase();
    }
  }, [language]);

  const resolveLanguageOption = (code: string) =>
    languageOptions.find((lang) => String(lang.code || '').toUpperCase() === String(code || '').toUpperCase()) ||
    FALLBACK_LANGUAGES.find((lang) => String(lang.code || '').toUpperCase() === String(code || '').toUpperCase());

  const resolveCurrencyOption = (code: string) =>
    currencyOptions.find((curr) => String(curr.code || '').toUpperCase() === String(code || '').toUpperCase()) ||
    FALLBACK_CURRENCIES.find((curr) => String(curr.code || '').toUpperCase() === String(code || '').toUpperCase());

  const handleSaveLanguageRegion = () => {
    const nextLanguage = resolveLanguageOption(pendingLanguage);
    const nextCurrency = resolveCurrencyOption(pendingCurrency);

    if (nextLanguage) {
      setLanguageSelection({ code: nextLanguage.code, name: nextLanguage.name });
    }
    if (nextCurrency) {
      setCurrencySelection({
        code: nextCurrency.code,
        name: nextCurrency.name,
        symbol: nextCurrency.symbol || '',
      });
    }

    setSaveMessage('Preferences saved.');
    setTimeout(() => setSaveMessage(null), 2000);
  };

  useEffect(() => {
    setPendingLanguage(language);
  }, [language]);

  useEffect(() => {
    setPendingCurrency(currency);
  }, [currency]);

  useEffect(() => {
    const loadPaymentMethods = async () => {
      try {
        const response = await api.masterData.paymentMethods.getAll();
        const payload = (response as any)?.data;
        const raw = Array.isArray(payload) ? payload : Array.isArray(payload?.data) ? payload.data : [];
        const mapped = raw
          .map((row: any) => {
            const meta = parseMetadata(row?.metadata);
            const code = String(row?.code || row?.payment_method_code || row?.name || '').toUpperCase();
            const name = String(row?.name || row?.payment_method_name || row?.label || '').trim();
            const type = String(row?.type || row?.category || row?.methodType || row?.payment_method_type || '').trim();
            return {
              id: String(row?.id || code || name),
              code,
              name: name || code,
              type: type || meta?.type || code,
              metadata: meta,
            } as PaymentMethodOption;
          })
          .filter((row: PaymentMethodOption) => row.id && row.name);

        setPaymentMethods(mapped.length ? mapped : FALLBACK_PAYMENT_METHODS);
        setPaymentLoadError(null);
      } catch (error: any) {
        setPaymentMethods(FALLBACK_PAYMENT_METHODS);
        setPaymentLoadError(error?.message || 'Unable to load payment methods');
      }
    };

    void loadPaymentMethods();
  }, []);

  useEffect(() => {
    try {
      const storedCards = localStorage.getItem(CARD_STORAGE_KEY);
      const parsed = storedCards ? JSON.parse(storedCards) : [];
      const normalized = (Array.isArray(parsed) ? parsed : []).map((card: any) => {
        const rawBrand = String(card?.brand || card?.type || '').toLowerCase();
        const brand = rawBrand.includes('visa')
          ? 'Visa'
          : rawBrand.includes('master')
          ? 'Mastercard'
          : rawBrand.includes('rupay') || rawBrand.includes('ru-pay')
          ? 'RuPay'
          : rawBrand.includes('amex')
          ? 'Amex'
          : rawBrand.includes('discover')
          ? 'Discover'
          : detectCardBrand(card?.number || '');
        return {
          id: String(card?.id || `card-${Date.now()}`),
          brand,
          last4: String(card?.last4 || '').slice(-4),
          expiry: String(card?.expiry || ''),
          isDefault: Boolean(card?.isDefault),
          holderName: card?.holderName || card?.name,
          nickname: card?.nickname || card?.name || '',
          billingAddress: card?.billingAddress || '',
          token: String(card?.token || tokenizeCard()),
          createdAt: String(card?.createdAt || new Date().toISOString()),
        } as SavedCard;
      });
      setSavedCardsState(normalized);
    } catch {
      setSavedCardsState([]);
    }
  }, [CARD_STORAGE_KEY]);

  useEffect(() => {
    try {
      const storedLinks = localStorage.getItem(PAYMENT_LINKS_KEY);
      const parsed = storedLinks ? JSON.parse(storedLinks) : {};
      const next = WALLET_PROVIDERS.reduce((acc, provider) => {
        const raw = parsed?.[provider.id] || {};
        acc[provider.id] = {
          linked: Boolean(raw?.linked),
          mobile: raw?.mobile || raw?.value || '',
          linkedAt: raw?.linkedAt || '',
          isDefault: Boolean(raw?.isDefault),
          balance: typeof raw?.balance === 'number' ? raw.balance : null,
          transactions: Array.isArray(raw?.transactions) ? raw.transactions : [],
          offers: Array.isArray(raw?.offers) ? raw.offers : [],
          deviceVerified: raw?.deviceVerified !== false,
          pinRequired: Boolean(raw?.pinRequired),
          autoPay: Boolean(raw?.autoPay),
        } as WalletLinkState;
        return acc;
      }, {} as Record<WalletProviderId, WalletLinkState>);
      setLinkedPayments(next);
    } catch {
      const next = WALLET_PROVIDERS.reduce((acc, provider) => {
        acc[provider.id] = {
          linked: false,
          mobile: '',
          linkedAt: '',
          isDefault: false,
          balance: null,
          transactions: [],
          offers: [],
          deviceVerified: true,
          pinRequired: false,
          autoPay: false,
        };
        return acc;
      }, {} as Record<WalletProviderId, WalletLinkState>);
      setLinkedPayments(next);
    }
  }, [PAYMENT_LINKS_KEY]);

  useEffect(() => {
    localStorage.setItem(CARD_STORAGE_KEY, JSON.stringify(savedCardsState));
  }, [CARD_STORAGE_KEY, savedCardsState]);

  useEffect(() => {
    localStorage.setItem(PAYMENT_LINKS_KEY, JSON.stringify(linkedPayments));
  }, [PAYMENT_LINKS_KEY, linkedPayments]);

  useEffect(() => {
    if (activeSection === 'addresses') {
      void loadSavedAddresses();
    }
  }, [activeSection]);

  const handleAddCard = () => {
    const cleanNumber = cardForm.number.replace(/\s+/g, '');
    const errors: Record<string, string> = {};
    const brand = detectCardBrand(cleanNumber);
    const isLengthValid =
      (brand === 'Amex' && cleanNumber.length === 15) ||
      (brand !== 'Amex' && cleanNumber.length === 16);

    if (!/^\d+$/.test(cleanNumber) || !isLengthValid || !luhnCheck(cleanNumber)) {
      errors.number = 'Invalid Card Number';
    }
    if (!cardForm.name.trim()) {
      errors.name = 'Cardholder name is required.';
    }
    if (!/^(0[1-9]|1[0-2])\/\d{2}$/.test(cardForm.expiry)) {
      errors.expiry = 'Expiry must be in MM/YY format.';
    } else if (!isExpiryValid(cardForm.expiry)) {
      errors.expiry = 'Card Expired';
    }
    if (!(brand === 'Amex' ? /^\d{4}$/ : /^\d{3}$/).test(cardForm.cvv)) {
      errors.cvv = 'Invalid CVV';
    }

    setCardErrors(errors);
    if (Object.keys(errors).length > 0) return;

    if (cardForm.saveCard) {
      const nickname =
        cardForm.nickname.trim() ||
        `${brand === 'Card' ? 'Personal' : brand} Card`;
      const newCard: SavedCard = {
        id: `card-${Date.now()}`,
        brand,
        last4: cleanNumber.slice(-4),
        expiry: cardForm.expiry,
        isDefault: cardForm.isDefault || savedCardsState.length === 0,
        holderName: cardForm.name.trim(),
        nickname,
        billingAddress: cardForm.billingAddress.trim(),
        token: tokenizeCard(),
        createdAt: new Date().toISOString(),
      };

      setSavedCardsState((prev) => {
        const next = newCard.isDefault ? prev.map((card) => ({ ...card, isDefault: false })) : prev;
        return [newCard, ...next];
      });
      setCardSuccessMessage('Card Added Successfully');
      setTimeout(() => setCardSuccessMessage(null), 2500);
    } else {
      setCardSuccessMessage('Card Verified Successfully');
      setTimeout(() => setCardSuccessMessage(null), 2000);
    }
    setShowAddCardModal(false);
    setCardForm({
      number: '',
      name: '',
      expiry: '',
      cvv: '',
      nickname: '',
      billingAddress: '',
      isDefault: false,
      saveCard: true,
    });
    setCardErrors({});
  };

  const handleRemoveCard = (cardId: string) => {
    setSavedCardsState((prev) => {
      const next = prev.filter((card) => card.id !== cardId);
      if (next.length > 0 && !next.some((card) => card.isDefault)) {
        next[0] = { ...next[0], isDefault: true };
      }
      return next;
    });
  };

  const handleSetDefaultCard = (cardId: string) => {
    setSavedCardsState((prev) => prev.map((card) => ({ ...card, isDefault: card.id === cardId })));
  };

  const openEditCardModal = (card: SavedCard) => {
    setEditingCard(card);
    setEditCardForm({
      nickname: card.nickname || '',
      expiry: card.expiry || '',
      billingAddress: card.billingAddress || '',
      isDefault: card.isDefault,
    });
    setEditErrors({});
  };

  const handleUpdateCard = () => {
    if (!editingCard) return;
    const errors: Record<string, string> = {};
    if (editCardForm.expiry && !isExpiryValid(editCardForm.expiry)) {
      errors.expiry = 'Card Expired';
    } else if (editCardForm.expiry && !/^(0[1-9]|1[0-2])\/\d{2}$/.test(editCardForm.expiry)) {
      errors.expiry = 'Expiry must be in MM/YY format.';
    }
    setEditErrors(errors);
    if (Object.keys(errors).length > 0) return;

    setSavedCardsState((prev) =>
      prev.map((card) => {
        if (card.id !== editingCard.id) {
          return editCardForm.isDefault ? { ...card, isDefault: false } : card;
        }
        return {
          ...card,
          nickname: editCardForm.nickname.trim() || card.nickname,
          expiry: editCardForm.expiry.trim() || card.expiry,
          billingAddress: editCardForm.billingAddress.trim(),
          isDefault: editCardForm.isDefault,
        };
      })
    );

    setEditingCard(null);
    setEditCardForm({
      nickname: '',
      expiry: '',
      billingAddress: '',
      isDefault: false,
    });
    setEditErrors({});
  };

  const getWalletBalance = (providerId: WalletProviderId) => {
    const map: Record<WalletProviderId, number> = {
      phonepe: 350,
      gpay: 420,
      paytm: 500,
    };
    return map[providerId];
  };

  const getWalletOffers = (providerId: WalletProviderId) => {
    const map: Record<WalletProviderId, string[]> = {
      phonepe: ['Get 5% cashback up to 50 on PhonePe'],
      gpay: ['Pay with Google Pay and get 20 off'],
      paytm: ['Pay with Paytm and get 50 cashback'],
    };
    return map[providerId];
  };

  const openWalletModal = (providerId: WalletProviderId, mode: 'link' | 'manage' = 'link') => {
    const current = linkedPayments[providerId];
    setWalletModalProvider(providerId);
    setWalletModalMode(mode);
    setWalletMobile(current?.mobile || '');
    setWalletOtp('');
    setWalletPin('');
    setWalletDeviceVerified(current?.deviceVerified !== false);
    setWalletPinRequired(Boolean(current?.pinRequired));
    setWalletLinkError('');
    setWalletOtpMessage('');
  };

  const closeWalletModal = () => {
    setWalletModalProvider(null);
    setWalletModalMode('link');
    setWalletMobile('');
    setWalletOtp('');
    setWalletPin('');
    setWalletDeviceVerified(true);
    setWalletPinRequired(false);
    setWalletLinkError('');
    setWalletOtpMessage('');
  };

  const handleSendWalletOtp = () => {
    if (!walletModalProvider) return;
    if (!/^[0-9]{10}$/.test(walletMobile.trim())) {
      setWalletLinkError('Enter a valid 10-digit mobile number.');
      return;
    }
    setWalletLinkError('');
    setWalletOtpMessage(`OTP sent to +91 ${walletMobile.trim()}. Use 123456 for demo.`);
    setWalletModalMode('otp');
  };

  const handleVerifyWalletOtp = () => {
    if (!walletModalProvider) return;
    if (!/^[0-9]{6}$/.test(walletOtp.trim())) {
      setWalletLinkError('Enter the 6-digit OTP.');
      return;
    }
    if (walletOtp.trim() !== '123456') {
      setWalletLinkError('Invalid OTP. Use 123456 for demo.');
      return;
    }
    setLinkedPayments((prev) => {
      const hasDefault = WALLET_PROVIDERS.some((wallet) => Boolean(prev[wallet.id]?.isDefault));
      const next = { ...prev };
      next[walletModalProvider] = {
        linked: true,
        mobile: walletMobile.trim(),
        linkedAt: new Date().toISOString(),
        isDefault: prev[walletModalProvider]?.isDefault || !hasDefault,
        balance: prev[walletModalProvider]?.balance ?? getWalletBalance(walletModalProvider),
        transactions: prev[walletModalProvider]?.transactions || [],
        offers: getWalletOffers(walletModalProvider),
        deviceVerified: walletDeviceVerified,
        pinRequired: walletPinRequired,
        autoPay: prev[walletModalProvider]?.autoPay || false,
      };
      return next;
    });
    setWalletModalMode('manage');
    setWalletLinkError('');
    setWalletOtpMessage('');
  };

  const handleUnlinkWallet = (providerId: WalletProviderId) => {
    const confirmed = window.confirm(`Are you sure you want to unlink ${providerId === 'gpay' ? 'Google Pay' : providerId.charAt(0).toUpperCase() + providerId.slice(1)}?`);
    if (!confirmed) return;
    setLinkedPayments((prev) => ({
      ...prev,
      [providerId]: {
        linked: false,
        mobile: '',
        linkedAt: '',
        isDefault: false,
        balance: null,
        transactions: prev[providerId]?.transactions || [],
        offers: prev[providerId]?.offers || [],
        deviceVerified: true,
        pinRequired: false,
        autoPay: false,
      },
    }));
    closeWalletModal();
  };

  const handleSetDefaultWallet = (providerId: WalletProviderId) => {
    setLinkedPayments((prev) => {
      const next = { ...prev };
      WALLET_PROVIDERS.forEach((wallet) => {
        next[wallet.id] = { ...next[wallet.id], isDefault: wallet.id === providerId };
      });
      return next;
    });
  };

  const handleToggleAutoPay = (providerId: WalletProviderId, enabled: boolean) => {
    setLinkedPayments((prev) => ({
      ...prev,
      [providerId]: {
        ...prev[providerId],
        autoPay: enabled,
      },
    }));
  };

  const handleChangePassword = async () => {
    setChangePasswordError('');
    setChangePasswordSuccess('');
    const { currentPassword, newPassword, confirmPassword } = changePasswordForm;
    if (!currentPassword.trim()) {
      setChangePasswordError('Current password is required.');
      return;
    }
    if (newPassword.trim().length < 8) {
      setChangePasswordError('New password must be at least 8 characters.');
      return;
    }
    if (newPassword !== confirmPassword) {
      setChangePasswordError('Passwords do not match.');
      return;
    }

    setChangePasswordLoading(true);
    try {
      await api.auth.resetPassword({
        currentPassword: currentPassword.trim(),
        newPassword: newPassword.trim(),
        confirmPassword: confirmPassword.trim(),
      });
      setChangePasswordSuccess('Password updated successfully.');
      setPasswordUpdateBanner('Password updated successfully.');
      setChangePasswordForm({
        currentPassword: '',
        newPassword: '',
        confirmPassword: '',
        showCurrent: false,
        showNew: false,
        showConfirm: false,
      });
      setTimeout(() => setShowChangePasswordModal(false), 1500);
      setTimeout(() => setPasswordUpdateBanner(null), 3000);
    } catch (error: any) {
      setChangePasswordError(error?.message || 'Unable to update password.');
    } finally {
      setChangePasswordLoading(false);
    }
  };

  const renderContent = () => {
    switch (activeSection) {
      case 'account':
        return (
          <div className="space-y-6">
            <div>
              <h2 className="text-2xl font-bold text-gray-900 mb-2">Account Settings</h2>
              <p className="text-gray-600">Manage your personal information</p>
            </div>

            {/* Profile Picture */}
            <div className="bg-white rounded-2xl p-6 border border-gray-200 shadow-sm">
              <div className="flex items-center gap-6">
                <div className="relative">
                  <div className="w-24 h-24 bg-gradient-to-br from-purple-500 to-pink-500 rounded-full flex items-center justify-center">
                    <span className="text-white text-3xl font-bold">
                      {user.name.charAt(0).toUpperCase()}
                    </span>
                  </div>
                  <button 
                    onClick={() => {
                      // Trigger file input for profile picture upload
                      const input = document.createElement('input');
                      input.type = 'file';
                      input.accept = 'image/*';
                      input.onchange = (e: Event) => {
                        const file = (e.target as HTMLInputElement).files?.[0];
                        if (file) {
                          // Handle profile picture upload
                          console.log('Profile picture selected:', file.name);
                          alert(`Profile picture "${file.name}" selected. In production, this would upload to the server.`);
                        }
                      };
                      input.click();
                    }}
                    className="absolute bottom-0 right-0 w-8 h-8 bg-blue-600 rounded-full flex items-center justify-center text-white hover:bg-blue-700 transition-colors shadow-lg"
                  >
                    <Edit className="w-4 h-4" />
                  </button>
                </div>
                <div className="flex-1">
                  <h3 className="text-xl font-bold text-gray-900">{user.name}</h3>
                  <p className="text-gray-600 mb-3">{user.email}</p>
                  <div className="flex items-center gap-3">
                    <span className="text-xs bg-gradient-to-r from-amber-400 to-orange-500 text-white px-3 py-1 rounded-full font-semibold flex items-center gap-1">
                      <Star className="w-3 h-3 fill-current" />
                      Gold Member
                    </span>
                    <span className="text-xs bg-green-100 text-green-700 px-3 py-1 rounded-full font-semibold">
                      125 Orders
                    </span>
                  </div>
                </div>
              </div>
            </div>

            {/* Profile Information */}
            <div className="bg-white rounded-2xl p-6 border border-gray-200 shadow-sm">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-bold text-gray-900">Personal Information</h3>
                {!isEditingProfile ? (
                  <button
                    onClick={() => setIsEditingProfile(true)}
                    className="text-blue-600 hover:text-blue-700 font-semibold flex items-center gap-2"
                  >
                    <Edit className="w-4 h-4" />
                    Edit
                  </button>
                ) : (
                  <div className="flex gap-2">
                    <button
                      onClick={() => setIsEditingProfile(false)}
                      className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors flex items-center gap-2"
                    >
                      <Check className="w-4 h-4" />
                      Save
                    </button>
                    <button
                      onClick={() => {
                        setIsEditingProfile(false);
                        setProfileData({
                          name: user?.name ?? '',
                          email: user?.email ?? '',
                          phone: user?.phone ?? '',
                          birthday: '15/08/1995',
                        });
                      }}
                      className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                )}
              </div>

              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Full Name
                  </label>
                  {isEditingProfile ? (
                    <input
                      type="text"
                      value={profileData.name || ''}
                      onChange={(e) => setProfileData({ ...profileData, name: e.target.value })}
                      className="w-full px-4 py-3 border-2 border-gray-300 rounded-xl focus:border-blue-500 focus:outline-none transition-colors"
                    />
                  ) : (
                    <div className="flex items-center gap-3 px-4 py-3 bg-gray-50 rounded-xl">
                      <User className="w-5 h-5 text-gray-400" />
                      <span className="text-gray-900 font-medium">{profileData.name}</span>
                    </div>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Email Address
                  </label>
                  {isEditingProfile ? (
                    <input
                      type="email"
                      value={profileData.email || ''}
                      onChange={(e) => setProfileData({ ...profileData, email: e.target.value })}
                      className="w-full px-4 py-3 border-2 border-gray-300 rounded-xl focus:border-blue-500 focus:outline-none transition-colors"
                    />
                  ) : (
                    <div className="flex items-center gap-3 px-4 py-3 bg-gray-50 rounded-xl">
                      <Mail className="w-5 h-5 text-gray-400" />
                      <span className="text-gray-900 font-medium">{profileData.email}</span>
                    </div>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Phone Number
                  </label>
                  {isEditingProfile ? (
                    <input
                      type="tel"
                      value={profileData.phone || ''}
                      onChange={(e) => setProfileData({ ...profileData, phone: e.target.value })}
                      className="w-full px-4 py-3 border-2 border-gray-300 rounded-xl focus:border-blue-500 focus:outline-none transition-colors"
                    />
                  ) : (
                    <div className="flex items-center gap-3 px-4 py-3 bg-gray-50 rounded-xl">
                      <Phone className="w-5 h-5 text-gray-400" />
                      <span className="text-gray-900 font-medium">{profileData.phone || 'Not provided'}</span>
                    </div>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Birthday
                  </label>
                  {isEditingProfile ? (
                    <input
                      type="text"
                      value={profileData.birthday || ''}
                      onChange={(e) => setProfileData({ ...profileData, birthday: e.target.value })}
                      placeholder="DD/MM/YYYY"
                      className="w-full px-4 py-3 border-2 border-gray-300 rounded-xl focus:border-blue-500 focus:outline-none transition-colors"
                    />
                  ) : (
                    <div className="flex items-center gap-3 px-4 py-3 bg-gray-50 rounded-xl">
                      <Calendar className="w-5 h-5 text-gray-400" />
                      <span className="text-gray-900 font-medium">{profileData.birthday}</span>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Account Actions */}
            <div className="bg-white rounded-2xl p-6 border border-gray-200 shadow-sm space-y-3">
              <h3 className="text-lg font-bold text-gray-900 mb-4">Account Actions</h3>
              {passwordUpdateBanner && (
                <div className="text-sm text-green-700 bg-green-50 border border-green-200 rounded-lg px-4 py-2">
                  {passwordUpdateBanner}
                </div>
              )}
              
              <button
                onClick={() => {
                  setChangePasswordError('');
                  setChangePasswordSuccess('');
                  setShowChangePasswordModal(true);
                }}
                className="w-full flex items-center justify-between p-4 rounded-xl hover:bg-gray-50 transition-colors group"
              >
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center">
                    <Lock className="w-5 h-5 text-blue-600" />
                  </div>
                  <div className="text-left">
                    <p className="font-semibold text-gray-900">Change Password</p>
                    <p className="text-sm text-gray-600">Update your password</p>
                  </div>
                </div>
                <ChevronRight className="w-5 h-5 text-gray-400 group-hover:text-gray-600 transition-colors" />
              </button>

              <button 
                onClick={() => {
                  // Simulate data export
                  const userData = {
                    name: user.name,
                    email: user.email,
                    phone: user?.phone ?? 'Not provided',
                    memberSince: '2024',
                    totalOrders: 125,
                    membershipLevel: 'Gold'
                  };
                  const dataStr = JSON.stringify(userData, null, 2);
                  const dataBlob = new Blob([dataStr], { type: 'application/json' });
                  const url = URL.createObjectURL(dataBlob);
                  const link = document.createElement('a');
                  link.href = url;
                  link.download = 'my-account-data.json';
                  link.click();
                  URL.revokeObjectURL(url);
                  alert('Your account data has been downloaded as a JSON file.');
                }}
                className="w-full flex items-center justify-between p-4 rounded-xl hover:bg-gray-50 transition-colors group"
              >
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-purple-100 rounded-full flex items-center justify-center">
                    <Download className="w-5 h-5 text-purple-600" />
                  </div>
                  <div className="text-left">
                    <p className="font-semibold text-gray-900">Download Data</p>
                    <p className="text-sm text-gray-600">Export your information</p>
                  </div>
                </div>
                <ChevronRight className="w-5 h-5 text-gray-400 group-hover:text-gray-600 transition-colors" />
              </button>

              <button 
                onClick={() => {
                  const confirmed = window.confirm(
                    '⚠️ WARNING: Are you sure you want to delete your account?\n\n' +
                    'This action is PERMANENT and cannot be undone. You will lose:\n' +
                    '• All your order history\n' +
                    '• Saved addresses and payment methods\n' +
                    '• Loyalty points and membership benefits\n' +
                    '• All personal data\n\n' +
                    'Type "DELETE" in the next prompt to confirm.'
                  );
                  if (confirmed) {
                    const confirmation = prompt('Type "DELETE" to permanently delete your account:');
                    if (confirmation === 'DELETE') {
                      alert('Account deletion confirmed. In production, this would permanently delete your account and log you out.');
                      // In production: call API to delete account, then logout
                      // onLogout();
                    } else {
                      alert('Account deletion cancelled. Your account is safe.');
                    }
                  }
                }}
                className="w-full flex items-center justify-between p-4 rounded-xl hover:bg-red-50 transition-colors group"
              >
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-red-100 rounded-full flex items-center justify-center">
                    <Trash2 className="w-5 h-5 text-red-600" />
                  </div>
                  <div className="text-left">
                    <p className="font-semibold text-red-600">Delete Account</p>
                    <p className="text-sm text-gray-600">Permanently remove your account</p>
                  </div>
                </div>
                <ChevronRight className="w-5 h-5 text-gray-400 group-hover:text-red-600 transition-colors" />
              </button>
            </div>
          </div>
        );

      case 'appearance':
        return (
          <div className="space-y-6">
            <div>
              <h2 className="text-2xl font-bold text-gray-900 mb-2">Appearance</h2>
              <p className="text-gray-600">Customize how the app looks</p>
            </div>

            {/* Theme Selection */}
            <div className="bg-white rounded-2xl p-6 border border-gray-200 shadow-sm">
              <h3 className="text-lg font-bold text-gray-900 mb-4">Theme</h3>
              <ThemeModeToggle />
              <p className="mt-4 text-sm text-gray-600">
                Active preference: <span className="font-semibold text-gray-900 capitalize">{themeMode}</span>
              </p>
            </div>

            {/* Display Options */}
            <div className="bg-white rounded-2xl p-6 border border-gray-200 shadow-sm">
              <h3 className="text-lg font-bold text-gray-900 mb-4">Display Options</h3>
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="font-semibold text-gray-900">Compact View</p>
                    <p className="text-sm text-gray-600">Show more content on screen</p>
                  </div>
                  <label className="relative inline-flex items-center cursor-pointer">
                    <input type="checkbox" className="sr-only peer" />
                    <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                  </label>
                </div>

                <div className="flex items-center justify-between">
                  <div>
                    <p className="font-semibold text-gray-900">Animations</p>
                    <p className="text-sm text-gray-600">Enable smooth transitions</p>
                  </div>
                  <label className="relative inline-flex items-center cursor-pointer">
                    <input type="checkbox" className="sr-only peer" defaultChecked />
                    <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                  </label>
                </div>

                <div className="flex items-center justify-between">
                  <div>
                    <p className="font-semibold text-gray-900">High Contrast</p>
                    <p className="text-sm text-gray-600">Better readability</p>
                  </div>
                  <label className="relative inline-flex items-center cursor-pointer">
                    <input type="checkbox" className="sr-only peer" />
                    <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                  </label>
                </div>
              </div>
            </div>

            {/* Font Size */}
            <div className="bg-white rounded-2xl p-6 border border-gray-200 shadow-sm">
              <h3 className="text-lg font-bold text-gray-900 mb-4">Font Size</h3>
              <div className="space-y-4">
                <div className="flex items-center justify-between text-gray-600">
                  <span className="text-sm">Small</span>
                  <span className="text-lg">Large</span>
                </div>
                <input
                  type="range"
                  min="12"
                  max="20"
                  defaultValue="16"
                  className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-blue-600"
                />
                <div className="text-center">
                  <p className="text-gray-900 font-medium">
                    Preview: The quick brown fox jumps over the lazy dog
                  </p>
                </div>
              </div>
            </div>
          </div>
        );

      case 'notifications':
        return (
          <div className="space-y-6">
            <div>
              <h2 className="text-2xl font-bold text-gray-900 mb-2">Notifications</h2>
              <p className="text-gray-600">Manage how you receive alerts</p>
            </div>

            {/* Order Updates */}
            <div className="bg-white rounded-2xl p-6 border border-gray-200 shadow-sm">
              <h3 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
                <Bell className="w-5 h-5 text-blue-600" />
                Order Updates
              </h3>
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="font-semibold text-gray-900">Order Status Updates</p>
                    <p className="text-sm text-gray-600">Get notified about your order</p>
                  </div>
                  <label className="relative inline-flex items-center cursor-pointer">
                    <input
                      type="checkbox"
                      checked={notifications.orderUpdates}
                      onChange={(e) =>
                        setNotifications({ ...notifications, orderUpdates: e.target.checked })
                      }
                      className="sr-only peer"
                    />
                    <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                  </label>
                </div>
              </div>
            </div>

            {/* Promotional */}
            <div className="bg-white rounded-2xl p-6 border border-gray-200 shadow-sm">
              <h3 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
                <Zap className="w-5 h-5 text-orange-600" />
                Promotional
              </h3>
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="font-semibold text-gray-900">Offers & Discounts</p>
                    <p className="text-sm text-gray-600">Special deals and promotions</p>
                  </div>
                  <label className="relative inline-flex items-center cursor-pointer">
                    <input
                      type="checkbox"
                      checked={notifications.offers}
                      onChange={(e) =>
                        setNotifications({ ...notifications, offers: e.target.checked })
                      }
                      className="sr-only peer"
                    />
                    <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                  </label>
                </div>

                <div className="flex items-center justify-between">
                  <div>
                    <p className="font-semibold text-gray-900">New Restaurants</p>
                    <p className="text-sm text-gray-600">Discover new places to order from</p>
                  </div>
                  <label className="relative inline-flex items-center cursor-pointer">
                    <input
                      type="checkbox"
                      checked={notifications.newRestaurants}
                      onChange={(e) =>
                        setNotifications({ ...notifications, newRestaurants: e.target.checked })
                      }
                      className="sr-only peer"
                    />
                    <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                  </label>
                </div>
              </div>
            </div>

            {/* Notification Channels */}
            <div className="bg-white rounded-2xl p-6 border border-gray-200 shadow-sm">
              <h3 className="text-lg font-bold text-gray-900 mb-4">Notification Channels</h3>
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center">
                      <Smartphone className="w-5 h-5 text-blue-600" />
                    </div>
                    <div>
                      <p className="font-semibold text-gray-900">Push Notifications</p>
                      <p className="text-sm text-gray-600">On your device</p>
                    </div>
                  </div>
                  <label className="relative inline-flex items-center cursor-pointer">
                    <input
                      type="checkbox"
                      checked={notifications.push}
                      onChange={(e) =>
                        setNotifications({ ...notifications, push: e.target.checked })
                      }
                      className="sr-only peer"
                    />
                    <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                  </label>
                </div>

                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-purple-100 rounded-full flex items-center justify-center">
                      <Mail className="w-5 h-5 text-purple-600" />
                    </div>
                    <div>
                      <p className="font-semibold text-gray-900">Email</p>
                      <p className="text-sm text-gray-600">{user.email}</p>
                    </div>
                  </div>
                  <label className="relative inline-flex items-center cursor-pointer">
                    <input
                      type="checkbox"
                      checked={notifications.email}
                      onChange={(e) =>
                        setNotifications({ ...notifications, email: e.target.checked })
                      }
                      className="sr-only peer"
                    />
                    <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                  </label>
                </div>

                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center">
                      <MessageSquare className="w-5 h-5 text-green-600" />
                    </div>
                    <div>
                      <p className="font-semibold text-gray-900">SMS</p>
                      <p className="text-sm text-gray-600">{user.phone || '+91 98765 43210'}</p>
                    </div>
                  </div>
                  <label className="relative inline-flex items-center cursor-pointer">
                    <input
                      type="checkbox"
                      checked={notifications.sms}
                      onChange={(e) =>
                        setNotifications({ ...notifications, sms: e.target.checked })
                      }
                      className="sr-only peer"
                    />
                    <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                  </label>
                </div>

                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-orange-100 rounded-full flex items-center justify-center">
                      {notifications.sound ? (
                        <Volume2 className="w-5 h-5 text-orange-600" />
                      ) : (
                        <VolumeX className="w-5 h-5 text-orange-600" />
                      )}
                    </div>
                    <div>
                      <p className="font-semibold text-gray-900">Sound</p>
                      <p className="text-sm text-gray-600">Play notification sound</p>
                    </div>
                  </div>
                  <label className="relative inline-flex items-center cursor-pointer">
                    <input
                      type="checkbox"
                      checked={notifications.sound}
                      onChange={(e) =>
                        setNotifications({ ...notifications, sound: e.target.checked })
                      }
                      className="sr-only peer"
                    />
                    <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                  </label>
                </div>
              </div>
            </div>
          </div>
        );

      case 'privacy':
        return (
          <div className="space-y-6">
            <div>
              <h2 className="text-2xl font-bold text-gray-900 mb-2">Privacy & Security</h2>
              <p className="text-gray-600">Control your data and privacy</p>
            </div>

            {/* Privacy Settings */}
            <div className="bg-white rounded-2xl p-6 border border-gray-200 shadow-sm">
              <h3 className="text-lg font-bold text-gray-900 mb-4">Privacy Controls</h3>
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="font-semibold text-gray-900">Share Usage Data</p>
                    <p className="text-sm text-gray-600">Help improve the app</p>
                  </div>
                  <label className="relative inline-flex items-center cursor-pointer">
                    <input
                      type="checkbox"
                      checked={privacy.shareData}
                      onChange={(e) => setPrivacy({ ...privacy, shareData: e.target.checked })}
                      className="sr-only peer"
                    />
                    <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                  </label>
                </div>

                <div className="flex items-center justify-between">
                  <div>
                    <p className="font-semibold text-gray-900">Location Access</p>
                    <p className="text-sm text-gray-600">Allow app to access your location</p>
                  </div>
                  <label className="relative inline-flex items-center cursor-pointer">
                    <input
                      type="checkbox"
                      checked={privacy.locationAccess}
                      onChange={(e) => setPrivacy({ ...privacy, locationAccess: e.target.checked })}
                      className="sr-only peer"
                    />
                    <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                  </label>
                </div>

                <div className="flex items-center justify-between">
                  <div>
                    <p className="font-semibold text-gray-900">Activity Tracking</p>
                    <p className="text-sm text-gray-600">Track app usage for better experience</p>
                  </div>
                  <label className="relative inline-flex items-center cursor-pointer">
                    <input
                      type="checkbox"
                      checked={privacy.activityTracking}
                      onChange={(e) =>
                        setPrivacy({ ...privacy, activityTracking: e.target.checked })
                      }
                      className="sr-only peer"
                    />
                    <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                  </label>
                </div>

                <div className="flex items-center justify-between">
                  <div>
                    <p className="font-semibold text-gray-900">Personalized Ads</p>
                    <p className="text-sm text-gray-600">Show relevant advertisements</p>
                  </div>
                  <label className="relative inline-flex items-center cursor-pointer">
                    <input
                      type="checkbox"
                      checked={privacy.personalizedAds}
                      onChange={(e) =>
                        setPrivacy({ ...privacy, personalizedAds: e.target.checked })
                      }
                      className="sr-only peer"
                    />
                    <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                  </label>
                </div>
              </div>
            </div>

            {/* Security */}
            <div className="bg-white rounded-2xl p-6 border border-gray-200 shadow-sm">
              <h3 className="text-lg font-bold text-gray-900 mb-4">Security</h3>
              <div className="space-y-3">
                <button className="w-full flex items-center justify-between p-4 rounded-xl hover:bg-gray-50 transition-colors group">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center">
                      <Lock className="w-5 h-5 text-blue-600" />
                    </div>
                    <div className="text-left">
                      <p className="font-semibold text-gray-900">Two-Factor Authentication</p>
                      <p className="text-sm text-gray-600">Add extra security layer</p>
                    </div>
                  </div>
                  <ChevronRight className="w-5 h-5 text-gray-400 group-hover:text-gray-600" />
                </button>

                <button className="w-full flex items-center justify-between p-4 rounded-xl hover:bg-gray-50 transition-colors group">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-purple-100 rounded-full flex items-center justify-center">
                      <Eye className="w-5 h-5 text-purple-600" />
                    </div>
                    <div className="text-left">
                      <p className="font-semibold text-gray-900">Active Sessions</p>
                      <p className="text-sm text-gray-600">Manage logged-in devices</p>
                    </div>
                  </div>
                  <ChevronRight className="w-5 h-5 text-gray-400 group-hover:text-gray-600" />
                </button>
              </div>
            </div>

            {/* Data Management */}
            <div className="bg-white rounded-2xl p-6 border border-gray-200 shadow-sm">
              <h3 className="text-lg font-bold text-gray-900 mb-4">Data Management</h3>
              <div className="space-y-3">
                <button className="w-full flex items-center justify-between p-4 rounded-xl hover:bg-gray-50 transition-colors group">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center">
                      <Download className="w-5 h-5 text-green-600" />
                    </div>
                    <div className="text-left">
                      <p className="font-semibold text-gray-900">Download Your Data</p>
                      <p className="text-sm text-gray-600">Get a copy of your information</p>
                    </div>
                  </div>
                  <ChevronRight className="w-5 h-5 text-gray-400 group-hover:text-gray-600" />
                </button>

                <button className="w-full flex items-center justify-between p-4 rounded-xl hover:bg-red-50 transition-colors group">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-red-100 rounded-full flex items-center justify-center">
                      <Trash2 className="w-5 h-5 text-red-600" />
                    </div>
                    <div className="text-left">
                      <p className="font-semibold text-red-600">Clear Activity Data</p>
                      <p className="text-sm text-gray-600">Remove browsing history</p>
                    </div>
                  </div>
                  <ChevronRight className="w-5 h-5 text-gray-400 group-hover:text-red-600" />
                </button>
              </div>
            </div>
          </div>
        );

      case 'language':
        return (
          <div className="space-y-6">
            <div>
              <h2 className="text-2xl font-bold text-gray-900 mb-2">Language & Region</h2>
              <p className="text-gray-600">Customize language and currency</p>
            </div>

            {/* Language Selection */}
            <div className="bg-white rounded-2xl p-6 border border-gray-200 shadow-sm">
              <h3 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
                <Languages className="w-5 h-5 text-blue-600" />
                Language
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {languageOptions.map((lang) => (
                  <button
                    key={lang.code}
                    onClick={() => {
                      setPendingLanguage(String(lang.code || '').toUpperCase());
                    }}
                    className={`flex items-center gap-3 p-4 rounded-xl border-2 transition-all ${
                      pendingLanguage === String(lang.code || '').toUpperCase()
                        ? 'border-blue-500 bg-blue-50'
                        : 'border-gray-200 hover:border-gray-300'
                    }`}
                  >
                    {lang.flag ? (
                      <span className="text-3xl">{lang.flag}</span>
                    ) : (
                      <span className="inline-flex items-center justify-center w-10 h-10 rounded-full bg-blue-100 text-blue-700 font-bold text-sm">
                        {lang.code}
                      </span>
                    )}
                    <div className="flex flex-col text-left">
                      <span className="font-semibold text-gray-900">{lang.name}</span>
                      {lang.nativeName && <span className="text-sm text-gray-600">{lang.nativeName}</span>}
                    </div>
                    {pendingLanguage === String(lang.code || '').toUpperCase() && (
                      <Check className="w-5 h-5 text-blue-600 ml-auto" />
                    )}
                  </button>
                ))}
              </div>
            </div>

            {/* Currency Selection */}
            <div className="bg-white rounded-2xl p-6 border border-gray-200 shadow-sm">
              <h3 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
                <DollarSign className="w-5 h-5 text-green-600" />
                Currency
              </h3>
              <div className="space-y-3">
                {currencyOptions.map((curr) => (
                  <button
                    key={curr.code}
                    onClick={() => {
                      setPendingCurrency(String(curr.code || '').toUpperCase());
                    }}
                    className={`w-full flex items-center justify-between p-4 rounded-xl border-2 transition-all ${
                      pendingCurrency === String(curr.code || '').toUpperCase()
                        ? 'border-blue-500 bg-blue-50'
                        : 'border-gray-200 hover:border-gray-300'
                    }`}
                  >
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center">
                        <span className="text-lg font-bold text-green-600">{curr.symbol || '¤'}</span>
                      </div>
                      <div className="text-left">
                        <p className="font-semibold text-gray-900">{curr.name}</p>
                        <p className="text-sm text-gray-600">{curr.code}</p>
                      </div>
                    </div>
                    {pendingCurrency === String(curr.code || '').toUpperCase() && (
                      <Check className="w-5 h-5 text-blue-600" />
                    )}
                  </button>
                ))}
              </div>
            </div>

            <div className="flex items-center justify-end gap-3">
              {saveMessage && <span className="text-sm text-green-700">{saveMessage}</span>}
              <button
                onClick={handleSaveLanguageRegion}
                disabled={pendingLanguage === language && pendingCurrency === currency}
                className="px-5 py-2.5 bg-blue-600 text-white rounded-lg font-semibold hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Save
              </button>
            </div>

            {masterLoadError && (
              <div className="text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-4 py-2">
                {masterLoadError}
              </div>
            )}
          </div>
        );

      case 'payment': {
        const cardEnabled = paymentMethods.some((method) => normalizePaymentType(method.type) === 'card');
        const walletMethods = paymentMethods.filter((method) => normalizePaymentType(method.type) === 'wallet');
        const upiMethods = paymentMethods.filter((method) => normalizePaymentType(method.type) === 'upi');
        const otherMethods = paymentMethods.filter((method) => {
          const type = normalizePaymentType(method.type);
          return type !== 'card' && type !== 'wallet' && type !== 'upi';
        });

        return (
          <div className="space-y-6">
            <div>
              <h2 className="text-2xl font-bold text-gray-900 mb-2">Payment Methods</h2>
              <p className="text-gray-600">Manage your cards and wallets</p>
            </div>

            {/* Saved Cards */}
            {cardEnabled && (
              <div className="bg-white rounded-2xl p-6 border border-gray-200 shadow-sm">
                <div className="flex items-center justify-between mb-4">
                  <div>
                    <h3 className="text-lg font-bold text-gray-900">Saved Cards</h3>
                    <p className="text-sm text-gray-600">Tokenized cards with masked numbers</p>
                  </div>
                  <button
                    onClick={() => {
                      setCardErrors({});
                      setCardForm({
                        number: '',
                        name: '',
                        expiry: '',
                        cvv: '',
                        nickname: '',
                        billingAddress: '',
                        isDefault: false,
                        saveCard: true,
                      });
                      setShowAddCardModal(true);
                    }}
                    className="px-4 py-2 bg-blue-600 text-white rounded-lg font-semibold hover:bg-blue-700 transition-colors"
                  >
                    Add Card
                  </button>
                </div>

                {cardSuccessMessage && (
                  <div className="mb-4 text-sm text-green-700 bg-green-50 border border-green-200 rounded-lg px-4 py-2">
                    {cardSuccessMessage}
                  </div>
                )}

                <div className="space-y-3">
                  {savedCards.length === 0 && (
                    <div className="text-sm text-gray-500 bg-gray-50 rounded-xl p-4 border border-dashed border-gray-200">
                      No cards saved yet.
                    </div>
                  )}
                  {savedCards.map((card) => {
                    const brandStyles = getBrandStyles(card.brand);
                    return (
                      <div
                        key={card.id}
                        className="flex items-center gap-4 p-4 rounded-xl border-2 border-gray-200 hover:border-blue-300 transition-colors"
                      >
                        <div
                          className={`w-12 h-12 rounded-xl flex items-center justify-center ${brandStyles.bg} ${brandStyles.text}`}
                        >
                          <span className="text-xs font-bold">{brandStyles.label}</span>
                        </div>
                        <div className="flex-1">
                          <p className="font-semibold text-gray-900">
                            {card.nickname || `${card.brand} Card`} - {maskCardNumber(card.last4)}
                          </p>
                          <p className="text-sm text-gray-600">
                            Expires {card.expiry || '--/--'} - {card.holderName || 'Cardholder'}
                          </p>
                        </div>
                        <div className="flex items-center gap-2">
                          {card.isDefault ? (
                            <span className="text-xs bg-green-100 text-green-700 px-3 py-1 rounded-full font-semibold">
                              Default
                            </span>
                          ) : (
                            <button
                              onClick={() => handleSetDefaultCard(card.id)}
                              className="text-xs text-blue-600 font-semibold hover:text-blue-700"
                            >
                              Set Default
                            </button>
                          )}
                          <button
                            onClick={() => openEditCardModal(card)}
                            className="text-gray-500 hover:text-gray-700"
                          >
                            <Edit className="w-5 h-5" />
                          </button>
                          <button
                            onClick={() => handleRemoveCard(card.id)}
                            className="text-red-600 hover:text-red-700"
                          >
                            <Trash2 className="w-5 h-5" />
                          </button>
                        </div>
                      </div>
                    );
                  })}
                </div>

                <div className="mt-4 text-xs text-gray-500">
                  We never store full card numbers. Your card is tokenized and protected with PCI-DSS compliant storage.
                </div>
              </div>
            )}
            {/* Digital Wallets */}
            <div className="bg-white rounded-2xl p-6 border border-gray-200 shadow-sm">
              <h3 className="text-lg font-bold text-gray-900 mb-4">Digital Wallets</h3>
              <div className="space-y-4">
                {WALLET_PROVIDERS.map((wallet) => {
                  const state = linkedPayments[wallet.id];
                  const isLinked = Boolean(state?.linked);
                  const statusLabel = isLinked ? 'Linked' : 'Not linked';
                  return (
                    <div
                      key={wallet.id}
                      className="w-full flex items-center justify-between p-4 rounded-xl border-2 border-gray-200 hover:border-blue-300 transition-colors"
                    >
                      <div className="flex items-center gap-3">
                        <div className={`w-10 h-10 bg-gradient-to-br ${wallet.accent} rounded-xl flex items-center justify-center`}>
                          <span className="text-white font-bold text-lg">{wallet.name[0]}</span>
                        </div>
                        <div className="text-left">
                          <p className="font-semibold text-gray-900">{wallet.name}</p>
                          <p className="text-sm text-gray-600">{statusLabel}{isLinked ? ' (verified)' : ''}</p>
                          {isLinked && typeof state?.balance === 'number' && (
                            <p className="text-xs text-gray-500">Balance: {formatMoney(state.balance)}</p>
                          )}
                        </div>
                      </div>
                      <div className="flex items-center gap-3">
                        {isLinked && (
                          <label className="flex items-center gap-2 text-xs text-gray-600">
                            <input
                              type="checkbox"
                              checked={Boolean(state?.isDefault)}
                              onChange={(e) => {
                                if (e.target.checked) {
                                  handleSetDefaultWallet(wallet.id);
                                } else {
                                  setLinkedPayments((prev) => ({
                                    ...prev,
                                    [wallet.id]: { ...prev[wallet.id], isDefault: false },
                                  }));
                                }
                              }}
                              className="h-4 w-4 text-blue-600"
                            />
                            Use as default
                          </label>
                        )}
                        <button
                          onClick={() => openWalletModal(wallet.id, isLinked ? 'manage' : 'link')}
                          className="text-blue-600 font-semibold hover:text-blue-700"
                        >
                          {isLinked ? 'Manage' : 'Link'}
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Wallet Offers */}
            <div className="bg-white rounded-2xl p-6 border border-gray-200 shadow-sm">
              <h3 className="text-lg font-bold text-gray-900 mb-4">Wallet Offers</h3>
              <div className="space-y-2">
                {WALLET_PROVIDERS.flatMap((wallet) =>
                  (linkedPayments[wallet.id]?.offers || []).map((offer, index) => (
                    <div
                      key={`${wallet.id}-offer-${index}`}
                      className="flex items-center justify-between p-3 rounded-lg bg-blue-50 border border-blue-100"
                    >
                      <p className="text-sm text-gray-700">{offer}</p>
                      <span className="text-xs font-semibold text-blue-700">{wallet.name}</span>
                    </div>
                  ))
                )}
                {WALLET_PROVIDERS.every((wallet) => (linkedPayments[wallet.id]?.offers || []).length === 0) && (
                  <p className="text-sm text-gray-500">No offers available yet.</p>
                )}
              </div>
            </div>

            {/* Wallet Transactions */}
            <div className="bg-white rounded-2xl p-6 border border-gray-200 shadow-sm">
              <h3 className="text-lg font-bold text-gray-900 mb-4">Wallet Transactions</h3>
              <div className="space-y-2">
                {WALLET_PROVIDERS.flatMap((wallet) => linkedPayments[wallet.id]?.transactions || []).length === 0 && (
                  <p className="text-sm text-gray-500">No wallet transactions yet.</p>
                )}
                {WALLET_PROVIDERS.flatMap((wallet) => linkedPayments[wallet.id]?.transactions || []).map((tx) => (
                  <div
                    key={tx.id}
                    className="flex items-center justify-between p-3 rounded-lg border border-gray-200"
                  >
                    <div>
                      <p className="text-sm font-semibold text-gray-900">Order #{tx.orderId}</p>
                      <p className="text-xs text-gray-500">{new Date(tx.createdAt).toLocaleString()}</p>
                    </div>
                    <div className="text-right">
                      <p className="text-sm font-semibold text-gray-900">{formatMoney(tx.amount)}</p>
                      <p className="text-xs text-gray-500">
                        {tx.provider === 'gpay' ? 'Google Pay' : tx.provider.charAt(0).toUpperCase() + tx.provider.slice(1)}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        );
      }
      case 'addresses':
        return (
          <div className="space-y-6">
            <div>
              <h2 className="text-2xl font-bold text-gray-900 mb-2">Saved Addresses</h2>
              <p className="text-gray-600">Manage your delivery locations</p>
            </div>

            {/* Addresses List */}
            <div className="space-y-4">
              {savedAddresses.map((address) => {
                const Icon =
                  address.type === 'Home' ? Home : address.type === 'Work' ? Briefcase : MapPin;
                const iconColor =
                  address.type === 'Home'
                    ? 'text-blue-600'
                    : address.type === 'Work'
                    ? 'text-purple-600'
                    : 'text-pink-600';
                const iconBg =
                  address.type === 'Home'
                    ? 'bg-blue-100'
                    : address.type === 'Work'
                    ? 'bg-purple-100'
                    : 'bg-pink-100';

                return (
                  <div
                    key={address.id}
                    className="bg-white rounded-2xl p-6 border border-gray-200 shadow-sm"
                  >
                    <div className="flex items-start gap-4">
                      <div className={`w-12 h-12 ${iconBg} rounded-xl flex items-center justify-center flex-shrink-0`}>
                        <Icon className={`w-6 h-6 ${iconColor}`} />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-2">
                          <h3 className="font-bold text-gray-900">{address.type}</h3>
                          {address.isPrimary && (
                            <span className="text-xs bg-green-100 text-green-700 px-2 py-1 rounded-full font-semibold">
                              Primary
                            </span>
                          )}
                        </div>
                        <p className="text-gray-700 font-medium mb-1">{address.address}</p>
                        <p className="text-sm text-gray-600">{address.city}</p>
                      </div>
                      <div className="flex gap-2">
                        <button
                          type="button"
                          onClick={() => handleEditAddress(address)}
                          className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center hover:bg-blue-200 transition-colors"
                        >
                          <Edit className="w-5 h-5 text-blue-600" />
                        </button>
                        <button
                          type="button"
                          onClick={() => handleDeleteAddress(address.id)}
                          className="w-10 h-10 bg-red-100 rounded-full flex items-center justify-center hover:bg-red-200 transition-colors"
                        >
                          <Trash2 className="w-5 h-5 text-red-600" />
                        </button>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Add New Address */}
            <button
              onClick={() => {
                const primary = savedAddresses.find((item) => item.isPrimary && item.lat && item.lng);
                const fallback = savedAddresses.find((item) => item.lat && item.lng);
                if (primary?.lat && primary?.lng) {
                  setMapInitialLocation({ lat: primary.lat, lng: primary.lng });
                } else if (fallback?.lat && fallback?.lng) {
                  setMapInitialLocation({ lat: fallback.lat, lng: fallback.lng });
                }
                if (navigator.geolocation) {
                  navigator.geolocation.getCurrentPosition(
                    (position) => {
                      const { latitude, longitude } = position.coords;
                      setMapInitialLocation({ lat: latitude, lng: longitude });
                    },
                    () => {
                      // keep current initial location
                    }
                  );
                }
                setShowMapSelector(true);
              }}
              className="w-full py-4 px-6 border-2 border-dashed border-gray-300 rounded-2xl text-gray-600 hover:border-blue-400 hover:text-blue-600 hover:bg-blue-50 transition-all font-semibold flex items-center justify-center gap-2 text-lg"
            >
              <MapPin className="w-6 h-6" />
              Add New Address
            </button>
          </div>
        );

      case 'offers':
        return (
          <div className="space-y-6">
            <div>
              <h2 className="text-2xl font-bold text-gray-900 mb-2">Offers & Rewards</h2>
              <p className="text-gray-600">Coupons, cashback, and rewards points</p>
            </div>

            <div className="bg-white rounded-2xl p-6 border border-gray-200 shadow-sm">
              <h3 className="text-lg font-bold text-gray-900 mb-4">Your Rewards</h3>
              <div className="flex items-center justify-between p-4 rounded-xl bg-green-50 border border-green-200">
                <div>
                  <p className="text-sm text-gray-600">Available Points</p>
                  <p className="text-2xl font-bold text-green-700">{rewardPoints.toLocaleString()}</p>
                </div>
                <button
                  onClick={() => {
                    if (rewardPoints < 100) {
                      setOffersMessage('You need at least 100 points to redeem.');
                      return;
                    }
                    setRewardPoints((prev) => prev - 100);
                    setOffersMessage('✅ Redeemed 100 points successfully.');
                    setTimeout(() => setOffersMessage(null), 2500);
                  }}
                  className="px-4 py-2 bg-green-600 text-white rounded-lg font-semibold hover:bg-green-700 transition-colors"
                >
                  Redeem
                </button>
              </div>
            </div>

            <div className="bg-white rounded-2xl p-6 border border-gray-200 shadow-sm">
              <h3 className="text-lg font-bold text-gray-900 mb-4">Available Offers</h3>
              {offersMessage && (
                <div className="mb-3 text-sm text-blue-700 bg-blue-50 border border-blue-200 rounded-lg px-4 py-2">
                  {offersMessage}
                </div>
              )}
              <div className="space-y-3">
                {[
                  { title: 'FLAT50', desc: 'Flat 50 off on orders above 299' },
                  { title: 'FREEDLV', desc: 'Free delivery on your next order' },
                  { title: 'CASHBACK20', desc: '20 cashback via Paytm wallet' },
                ].map((offer) => (
                  <div
                    key={offer.title}
                    className="flex items-center justify-between p-4 rounded-xl border-2 border-gray-200 hover:border-blue-300 transition-colors"
                  >
                    <div>
                      <p className="font-semibold text-gray-900">{offer.title}</p>
                      <p className="text-sm text-gray-600">{offer.desc}</p>
                    </div>
                    <button
                      onClick={() => {
                        setOffersMessage(`✅ ${offer.title} applied successfully.`);
                        setTimeout(() => setOffersMessage(null), 2500);
                      }}
                      className="text-blue-600 font-semibold hover:text-blue-700"
                    >
                      Apply
                    </button>
                  </div>
                ))}
              </div>
            </div>

            <div className="bg-white rounded-2xl p-6 border border-gray-200 shadow-sm">
              <h3 className="text-lg font-bold text-gray-900 mb-4">Wallet Offers</h3>
              <div className="space-y-2">
                {WALLET_PROVIDERS.flatMap((wallet) =>
                  (linkedPayments[wallet.id]?.offers || []).map((offer, index) => (
                    <div
                      key={`${wallet.id}-offer-${index}`}
                      className="flex items-center justify-between p-3 rounded-lg bg-blue-50 border border-blue-100"
                    >
                      <p className="text-sm text-gray-700">{offer}</p>
                      <span className="text-xs font-semibold text-blue-700">{wallet.name}</span>
                    </div>
                  ))
                )}
                {WALLET_PROVIDERS.every((wallet) => (linkedPayments[wallet.id]?.offers || []).length === 0) && (
                  <p className="text-sm text-gray-500">No wallet offers available yet.</p>
                )}
              </div>
            </div>
          </div>
        );

      case 'preferences':
        return (
          <div className="space-y-6">
            <div>
              <h2 className="text-2xl font-bold text-gray-900 mb-2">Order Preferences</h2>
              <p className="text-gray-600">Set your default order options</p>
            </div>

            {/* Delivery Preferences */}
            <div className="bg-white rounded-2xl p-6 border border-gray-200 shadow-sm">
              <h3 className="text-lg font-bold text-gray-900 mb-4">Delivery Options</h3>
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="font-semibold text-gray-900">Contactless Delivery</p>
                    <p className="text-sm text-gray-600">Minimize physical contact</p>
                  </div>
                  <label className="relative inline-flex items-center cursor-pointer">
                    <input
                      type="checkbox"
                      checked={preferences.contactlessDelivery}
                      onChange={(e) =>
                        setPreferences({ ...preferences, contactlessDelivery: e.target.checked })
                      }
                      className="sr-only peer"
                    />
                    <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                  </label>
                </div>

                <div className="flex items-center justify-between">
                  <div>
                    <p className="font-semibold text-gray-900">Leave at Door</p>
                    <p className="text-sm text-gray-600">Don't ring the doorbell</p>
                  </div>
                  <label className="relative inline-flex items-center cursor-pointer">
                    <input
                      type="checkbox"
                      checked={preferences.leaveAtDoor}
                      onChange={(e) =>
                        setPreferences({ ...preferences, leaveAtDoor: e.target.checked })
                      }
                      className="sr-only peer"
                    />
                    <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                  </label>
                </div>
              </div>
            </div>

            {/* Dietary Preferences */}
            <div className="bg-white rounded-2xl p-6 border border-gray-200 shadow-sm">
              <h3 className="text-lg font-bold text-gray-900 mb-4">Dietary Preference</h3>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                {['all', 'veg', 'non-veg', 'jain', 'vegan'].map((diet) => (
                  <button
                    key={diet}
                    onClick={() =>
                      setPreferences({
                        ...preferences,
                        dietaryPreference: diet as any,
                      })
                    }
                    className={`p-4 rounded-xl border-2 transition-all ${
                      preferences.dietaryPreference === diet
                        ? 'border-green-500 bg-green-50'
                        : 'border-gray-200 hover:border-gray-300'
                    }`}
                  >
                    <p className="font-semibold text-gray-900 capitalize">{diet}</p>
                    {preferences.dietaryPreference === diet && (
                      <Check className="w-5 h-5 text-green-600 mx-auto mt-2" />
                    )}
                  </button>
                ))}
              </div>
            </div>

            {/* Spice Level */}
            <div className="bg-white rounded-2xl p-6 border border-gray-200 shadow-sm">
              <h3 className="text-lg font-bold text-gray-900 mb-4">Default Spice Level</h3>
              <div className="grid grid-cols-3 gap-3">
                {['mild', 'medium', 'hot'].map((spice) => (
                  <button
                    key={spice}
                    onClick={() =>
                      setPreferences({ ...preferences, spiceLevel: spice as any })
                    }
                    className={`p-4 rounded-xl border-2 transition-all ${
                      preferences.spiceLevel === spice
                        ? 'border-red-500 bg-red-50'
                        : 'border-gray-200 hover:border-gray-300'
                    }`}
                  >
                    <p className="font-semibold text-gray-900 capitalize">{spice}</p>
                    {preferences.spiceLevel === spice && (
                      <Check className="w-5 h-5 text-red-600 mx-auto mt-2" />
                    )}
                  </button>
                ))}
              </div>
            </div>

            {/* Default Tip */}
            <div className="bg-white rounded-2xl p-6 border border-gray-200 shadow-sm">
              <h3 className="text-lg font-bold text-gray-900 mb-4">Default Tip Amount</h3>
              <div className="grid grid-cols-4 gap-3">
                {[0, 10, 20, 30].map((tip) => (
                  <button
                    key={tip}
                    onClick={() => setPreferences({ ...preferences, defaultTip: tip })}
                    className={`p-4 rounded-xl border-2 transition-all ${
                      preferences.defaultTip === tip
                        ? 'border-green-500 bg-green-50'
                        : 'border-gray-200 hover:border-gray-300'
                    }`}
                  >
                    <p className="font-semibold text-gray-900">{formatMoney(tip)}</p>
                    {preferences.defaultTip === tip && (
                      <Check className="w-5 h-5 text-green-600 mx-auto mt-2" />
                    )}
                  </button>
                ))}
              </div>
            </div>
          </div>
        );

      case 'help':
        return (
          <div className="space-y-6">
            <div>
              <h2 className="text-2xl font-bold text-gray-900 mb-2">Help & Support</h2>
              <p className="text-gray-600">Get assistance and find answers</p>
            </div>

            {/* Quick Actions */}
            <div className="bg-white rounded-2xl p-6 border border-gray-200 shadow-sm">
              <h3 className="text-lg font-bold text-gray-900 mb-4">Quick Actions</h3>
              <div className="space-y-3">
                <button className="w-full flex items-center justify-between p-4 rounded-xl hover:bg-gray-50 transition-colors group">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center">
                      <MessageSquare className="w-5 h-5 text-blue-600" />
                    </div>
                    <div className="text-left">
                      <p className="font-semibold text-gray-900">Chat with Support</p>
                      <p className="text-sm text-gray-600">Get instant help</p>
                    </div>
                  </div>
                  <ChevronRight className="w-5 h-5 text-gray-400 group-hover:text-gray-600" />
                </button>

                <button className="w-full flex items-center justify-between p-4 rounded-xl hover:bg-gray-50 transition-colors group">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-purple-100 rounded-full flex items-center justify-center">
                      <Phone className="w-5 h-5 text-purple-600" />
                    </div>
                    <div className="text-left">
                      <p className="font-semibold text-gray-900">Call Support</p>
                      <p className="text-sm text-gray-600">+91 1800-1234-567</p>
                    </div>
                  </div>
                  <ChevronRight className="w-5 h-5 text-gray-400 group-hover:text-gray-600" />
                </button>

                <button className="w-full flex items-center justify-between p-4 rounded-xl hover:bg-gray-50 transition-colors group">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center">
                      <Mail className="w-5 h-5 text-green-600" />
                    </div>
                    <div className="text-left">
                      <p className="font-semibold text-gray-900">Email Us</p>
                      <p className="text-sm text-gray-600">support@foodapp.com</p>
                    </div>
                  </div>
                  <ChevronRight className="w-5 h-5 text-gray-400 group-hover:text-gray-600" />
                </button>
              </div>
            </div>

            {/* FAQs */}
            <div className="bg-white rounded-2xl p-6 border border-gray-200 shadow-sm">
              <h3 className="text-lg font-bold text-gray-900 mb-4">Common Questions</h3>
              <div className="space-y-3">
                <button className="w-full flex items-center justify-between p-4 rounded-xl hover:bg-gray-50 transition-colors group text-left">
                  <p className="font-semibold text-gray-900">How to track my order?</p>
                  <ChevronRight className="w-5 h-5 text-gray-400 group-hover:text-gray-600 flex-shrink-0" />
                </button>

                <button className="w-full flex items-center justify-between p-4 rounded-xl hover:bg-gray-50 transition-colors group text-left">
                  <p className="font-semibold text-gray-900">How to cancel an order?</p>
                  <ChevronRight className="w-5 h-5 text-gray-400 group-hover:text-gray-600 flex-shrink-0" />
                </button>

                <button className="w-full flex items-center justify-between p-4 rounded-xl hover:bg-gray-50 transition-colors group text-left">
                  <p className="font-semibold text-gray-900">Payment methods accepted?</p>
                  <ChevronRight className="w-5 h-5 text-gray-400 group-hover:text-gray-600 flex-shrink-0" />
                </button>

                <button className="w-full flex items-center justify-between p-4 rounded-xl hover:bg-gray-50 transition-colors group text-left">
                  <p className="font-semibold text-gray-900">How to get refund?</p>
                  <ChevronRight className="w-5 h-5 text-gray-400 group-hover:text-gray-600 flex-shrink-0" />
                </button>
              </div>
            </div>

            {/* Feedback */}
            <div className="bg-white rounded-2xl p-6 border border-gray-200 shadow-sm">
              <h3 className="text-lg font-bold text-gray-900 mb-4">Send Feedback</h3>
              <textarea
                placeholder="Tell us what you think..."
                rows={4}
                className="w-full px-4 py-3 border-2 border-gray-300 rounded-xl focus:border-blue-500 focus:outline-none transition-colors resize-none"
              />
              <button className="w-full mt-3 py-3 px-6 bg-gradient-to-r from-blue-600 to-purple-600 text-white rounded-xl font-semibold hover:shadow-lg transition-all">
                Submit Feedback
              </button>
            </div>
          </div>
        );

      case 'about':
        return (
          <div className="space-y-6">
            <div>
              <h2 className="text-2xl font-bold text-gray-900 mb-2">About</h2>
              <p className="text-gray-600">App information and legal</p>
            </div>

            {/* App Info */}
            <div className="bg-white rounded-2xl p-6 border border-gray-200 shadow-sm text-center">
              <div className="w-20 h-20 bg-gradient-to-br from-orange-400 to-pink-500 rounded-3xl flex items-center justify-center mx-auto mb-4">
                <Utensils className="w-10 h-10 text-white" />
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-1">Food Delivery App</h3>
              <p className="text-gray-600 mb-2">Version 2.5.0</p>
              <p className="text-sm text-gray-500">© 2024 Food Delivery Inc.</p>
            </div>

            {/* Legal */}
            <div className="bg-white rounded-2xl p-6 border border-gray-200 shadow-sm">
              <h3 className="text-lg font-bold text-gray-900 mb-4">Legal</h3>
              <div className="space-y-3">
                <button className="w-full flex items-center justify-between p-4 rounded-xl hover:bg-gray-50 transition-colors group">
                  <div className="flex items-center gap-3">
                    <FileText className="w-5 h-5 text-gray-600" />
                    <p className="font-semibold text-gray-900">Terms of Service</p>
                  </div>
                  <ChevronRight className="w-5 h-5 text-gray-400 group-hover:text-gray-600" />
                </button>

                <button className="w-full flex items-center justify-between p-4 rounded-xl hover:bg-gray-50 transition-colors group">
                  <div className="flex items-center gap-3">
                    <Shield className="w-5 h-5 text-gray-600" />
                    <p className="font-semibold text-gray-900">Privacy Policy</p>
                  </div>
                  <ChevronRight className="w-5 h-5 text-gray-400 group-hover:text-gray-600" />
                </button>

                <button className="w-full flex items-center justify-between p-4 rounded-xl hover:bg-gray-50 transition-colors group">
                  <div className="flex items-center gap-3">
                    <FileText className="w-5 h-5 text-gray-600" />
                    <p className="font-semibold text-gray-900">Licenses</p>
                  </div>
                  <ChevronRight className="w-5 h-5 text-gray-400 group-hover:text-gray-600" />
                </button>
              </div>
            </div>

            {/* Social */}
            <div className="bg-white rounded-2xl p-6 border border-gray-200 shadow-sm">
              <h3 className="text-lg font-bold text-gray-900 mb-4">Connect With Us</h3>
              <div className="flex items-center justify-center gap-4">
                <button className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center hover:bg-blue-200 transition-colors">
                  <Share2 className="w-6 h-6 text-blue-600" />
                </button>
                <button className="w-12 h-12 bg-pink-100 rounded-full flex items-center justify-center hover:bg-pink-200 transition-colors">
                  <Heart className="w-6 h-6 text-pink-600" />
                </button>
                <button className="w-12 h-12 bg-purple-100 rounded-full flex items-center justify-center hover:bg-purple-200 transition-colors">
                  <Star className="w-6 h-6 text-purple-600" />
                </button>
              </div>
            </div>

            {/* Updates */}
            <div className="bg-gradient-to-r from-blue-50 to-purple-50 rounded-2xl p-6 border border-blue-200">
              <div className="flex items-start gap-3">
                <div className="w-10 h-10 bg-blue-600 rounded-full flex items-center justify-center flex-shrink-0">
                  <AlertCircle className="w-6 h-6 text-white" />
                </div>
                <div>
                  <h4 className="font-bold text-gray-900 mb-1">Check for Updates</h4>
                  <p className="text-sm text-gray-600 mb-3">
                    You're using the latest version
                  </p>
                  <button className="text-sm text-blue-600 font-semibold hover:text-blue-700">
                    Check Now →
                  </button>
                </div>
              </div>
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <>
      <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-0 md:p-4">
        <div className="bg-gray-50 w-full h-full md:h-[90vh] md:max-w-7xl md:rounded-3xl flex flex-col md:flex-row overflow-hidden shadow-2xl">
        {/* Sidebar */}
        <div className="w-full md:w-80 bg-white border-b md:border-b-0 md:border-r border-gray-200 overflow-y-auto flex-shrink-0">
          {/* Header */}
          <div className="p-6 border-b border-gray-200">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-gradient-to-br from-purple-500 to-pink-500 rounded-full flex items-center justify-center">
                  <Settings className="w-6 h-6 text-white" />
                </div>
                <h2 className="text-xl font-bold text-gray-900">Settings</h2>
              </div>
              <button
                onClick={onClose}
                className="w-10 h-10 bg-gray-100 rounded-full flex items-center justify-center hover:bg-gray-200 transition-colors md:hidden"
              >
                <X className="w-6 h-6 text-gray-600" />
              </button>
            </div>
          </div>

          {/* Menu Items */}
          <div className="p-4">
            {menuItems.map((item) => {
              const Icon = item.icon;
              return (
                <button
                  key={item.id}
                  onClick={() => setActiveSection(item.id as SettingsSection)}
                  className={`w-full flex items-center gap-3 p-4 rounded-xl transition-all mb-2 ${
                    activeSection === item.id
                      ? 'bg-gradient-to-r from-blue-600 to-purple-600 text-white shadow-lg'
                      : 'text-gray-700 hover:bg-gray-100'
                  }`}
                >
                  <Icon className="w-5 h-5 flex-shrink-0" />
                  <div className="text-left flex-1 min-w-0">
                    <p className="font-semibold truncate">{item.label}</p>
                    <p
                      className={`text-xs truncate ${
                        activeSection === item.id ? 'text-blue-100' : 'text-gray-500'
                      }`}
                    >
                      {item.description}
                    </p>
                  </div>
                  {activeSection === item.id && <ChevronRight className="w-5 h-5 flex-shrink-0" />}
                </button>
              );
            })}

            {/* Logout Button */}
            <button
              onClick={onLogout}
              className="w-full flex items-center gap-3 p-4 rounded-xl transition-all mt-4 text-red-600 hover:bg-red-50"
            >
              <LogOut className="w-5 h-5" />
              <span className="font-semibold">Logout</span>
            </button>
          </div>
        </div>

        {/* Content Area */}
        <div className="flex-1 overflow-y-auto">
          {/* Mobile Header */}
          <div className="md:hidden sticky top-0 bg-white border-b border-gray-200 p-4 flex items-center justify-between z-10">
            <h3 className="font-bold text-gray-900 text-lg">
              {menuItems.find((item) => item.id === activeSection)?.label}
            </h3>
            <button
              onClick={onClose}
              className="w-10 h-10 bg-gray-100 rounded-full flex items-center justify-center hover:bg-gray-200 transition-colors"
            >
              <X className="w-6 h-6 text-gray-600" />
            </button>
          </div>

          {/* Desktop Close Button */}
          <div className="hidden md:block sticky top-0 bg-gray-50 p-6 z-10">
            <button
              onClick={onClose}
              className="ml-auto flex items-center gap-2 px-4 py-2 bg-white border border-gray-200 rounded-xl hover:bg-gray-100 transition-colors"
            >
              <X className="w-5 h-5 text-gray-600" />
              <span className="font-semibold text-gray-700">Close</span>
            </button>
          </div>

          {/* Content */}
          <div className="p-6 pb-20">{renderContent()}</div>
        </div>
        </div>
      </div>

      {showAddCardModal && (
        <div className="fixed inset-0 bg-black/60 z-[60] flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg">
            <div className="flex items-center justify-between p-5 border-b border-gray-200">
              <div>
                <h3 className="text-lg font-bold text-gray-900">Add New Card</h3>
                <p className="text-sm text-gray-600">Securely save a card for future payments</p>
              </div>
              <button
                onClick={() => setShowAddCardModal(false)}
                className="w-10 h-10 rounded-full bg-gray-100 flex items-center justify-center hover:bg-gray-200"
              >
                <X className="w-5 h-5 text-gray-600" />
              </button>
            </div>
            <div className="p-5 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Card Number</label>
                <div className="flex items-center gap-3">
                  <input
                    type="text"
                    value={cardForm.number}
                    onChange={(e) => {
                      const formatted = formatCardNumber(e.target.value);
                      const error = validateCardNumber(formatted);
                      setCardForm((prev) => ({ ...prev, number: formatted }));
                      setCardErrors((prev) => ({ ...prev, number: error }));
                    }}
                    placeholder="1234 5678 9012 3456"
                    className={`w-full px-4 py-3 border rounded-lg focus:outline-none focus:ring-2 ${
                      cardErrors.number ? 'border-red-300 focus:ring-red-300' : 'border-gray-300 focus:ring-blue-500'
                    }`}
                  />
                  <span className={`px-2 py-1 rounded-md text-xs font-semibold ${getBrandStyles(detectCardBrand(cardForm.number)).bg} ${getBrandStyles(detectCardBrand(cardForm.number)).text}`}>
                    {getBrandStyles(detectCardBrand(cardForm.number)).label}
                  </span>
                </div>
                {cardErrors.number && <p className="text-xs text-red-600 mt-1">{cardErrors.number}</p>}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Cardholder Name</label>
                <input
                  type="text"
                  value={cardForm.name}
                  onChange={(e) => {
                    const value = e.target.value.toUpperCase();
                    setCardForm((prev) => ({ ...prev, name: value }));
                    setCardErrors((prev) => ({ ...prev, name: value.trim() ? '' : 'Cardholder name is required.' }));
                  }}
                  placeholder="JOHN DOE"
                  className={`w-full px-4 py-3 border rounded-lg focus:outline-none focus:ring-2 ${
                    cardErrors.name ? 'border-red-300 focus:ring-red-300' : 'border-gray-300 focus:ring-blue-500'
                  }`}
                />
                {cardErrors.name && <p className="text-xs text-red-600 mt-1">{cardErrors.name}</p>}
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Expiry Date</label>
                  <input
                    type="text"
                    value={cardForm.expiry}
                    onChange={(e) => {
                      let value = e.target.value.replace(/\D/g, '');
                      if (value.length >= 2) {
                        value = value.slice(0, 2) + '/' + value.slice(2, 4);
                      }
                      if (value.length <= 5) {
                        const error = validateExpiry(value);
                        setCardForm((prev) => ({ ...prev, expiry: value }));
                        setCardErrors((prev) => ({ ...prev, expiry: error }));
                      }
                    }}
                    placeholder="MM/YY"
                    className={`w-full px-4 py-3 border rounded-lg focus:outline-none focus:ring-2 ${
                      cardErrors.expiry ? 'border-red-300 focus:ring-red-300' : 'border-gray-300 focus:ring-blue-500'
                    }`}
                  />
                  {cardErrors.expiry && <p className="text-xs text-red-600 mt-1">{cardErrors.expiry}</p>}
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">CVV</label>
                  <input
                    type="password"
                    value={cardForm.cvv}
                    onChange={(e) => {
                      const value = e.target.value.replace(/\D/g, '').slice(0, 4);
                      const brand = detectCardBrand(cardForm.number);
                      const error = validateCvv(value, brand);
                      setCardForm((prev) => ({ ...prev, cvv: value }));
                      setCardErrors((prev) => ({ ...prev, cvv: error }));
                    }}
                    placeholder={detectCardBrand(cardForm.number) === 'Amex' ? '1234' : '123'}
                    className={`w-full px-4 py-3 border rounded-lg focus:outline-none focus:ring-2 ${
                      cardErrors.cvv ? 'border-red-300 focus:ring-red-300' : 'border-gray-300 focus:ring-blue-500'
                    }`}
                  />
                  {cardErrors.cvv && <p className="text-xs text-red-600 mt-1">{cardErrors.cvv}</p>}
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Card Nickname</label>
                <input
                  type="text"
                  value={cardForm.nickname}
                  onChange={(e) => setCardForm((prev) => ({ ...prev, nickname: e.target.value }))}
                  placeholder="Personal Card"
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Billing Address (Optional)</label>
                <textarea
                  rows={2}
                  value={cardForm.billingAddress}
                  onChange={(e) => setCardForm((prev) => ({ ...prev, billingAddress: e.target.value }))}
                  placeholder="Street, City, State, ZIP"
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none"
                />
              </div>

              <div className="flex items-center justify-between">
                <label className="flex items-center gap-3 text-sm text-gray-700">
                  <input
                    type="checkbox"
                    checked={cardForm.saveCard}
                    onChange={(e) => setCardForm((prev) => ({ ...prev, saveCard: e.target.checked }))}
                    className="h-4 w-4 text-blue-600"
                  />
                  Save this card for future payments
                </label>
                <label className="flex items-center gap-2 text-sm text-gray-700">
                  <input
                    type="checkbox"
                    checked={cardForm.isDefault}
                    onChange={(e) => setCardForm((prev) => ({ ...prev, isDefault: e.target.checked }))}
                    className="h-4 w-4 text-blue-600"
                  />
                  Set as default
                </label>
              </div>

              <div className="flex items-center justify-between bg-blue-50 border border-blue-200 rounded-lg px-4 py-2 text-xs text-blue-700">
                <span>Card details are encrypted and tokenized. We do not store raw numbers.</span>
                <button
                  onClick={() => alert('Card scanning is a premium feature and requires camera access.')}
                  className="text-blue-700 font-semibold hover:text-blue-800"
                >
                  Scan Card
                </button>
              </div>
            </div>

            <div className="p-5 pt-0">
              <button
                onClick={handleAddCard}
                className="w-full bg-blue-600 text-white py-3 rounded-lg hover:bg-blue-700 transition-colors font-medium"
              >
                Add Card
              </button>
            </div>
          </div>
        </div>
      )}

      {editingCard && (
        <div className="fixed inset-0 bg-black/60 z-[60] flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md">
            <div className="flex items-center justify-between p-5 border-b border-gray-200">
              <div>
                <h3 className="text-lg font-bold text-gray-900">Edit Card</h3>
                <p className="text-sm text-gray-600">{maskCardNumber(editingCard.last4)}</p>
              </div>
              <button
                onClick={() => setEditingCard(null)}
                className="w-10 h-10 rounded-full bg-gray-100 flex items-center justify-center hover:bg-gray-200"
              >
                <X className="w-5 h-5 text-gray-600" />
              </button>
            </div>
            <div className="p-5 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Card Nickname</label>
                <input
                  type="text"
                  value={editCardForm.nickname}
                  onChange={(e) => setEditCardForm((prev) => ({ ...prev, nickname: e.target.value }))}
                  placeholder="Personal Card"
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Expiry Date</label>
                <input
                  type="text"
                  value={editCardForm.expiry}
                  onChange={(e) => {
                    let value = e.target.value.replace(/\D/g, '');
                    if (value.length >= 2) {
                      value = value.slice(0, 2) + '/' + value.slice(2, 4);
                    }
                    if (value.length <= 5) {
                      const error = validateExpiry(value);
                      setEditCardForm((prev) => ({ ...prev, expiry: value }));
                      setEditErrors((prev) => ({ ...prev, expiry: error }));
                    }
                  }}
                  placeholder="MM/YY"
                  className={`w-full px-4 py-3 border rounded-lg focus:outline-none focus:ring-2 ${
                    editErrors.expiry ? 'border-red-300 focus:ring-red-300' : 'border-gray-300 focus:ring-blue-500'
                  }`}
                />
                {editErrors.expiry && <p className="text-xs text-red-600 mt-1">{editErrors.expiry}</p>}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Billing Address (Optional)</label>
                <textarea
                  rows={2}
                  value={editCardForm.billingAddress}
                  onChange={(e) => setEditCardForm((prev) => ({ ...prev, billingAddress: e.target.value }))}
                  placeholder="Street, City, State, ZIP"
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none"
                />
              </div>

              <label className="flex items-center gap-3 text-sm text-gray-700">
                <input
                  type="checkbox"
                  checked={editCardForm.isDefault}
                  onChange={(e) => setEditCardForm((prev) => ({ ...prev, isDefault: e.target.checked }))}
                  className="h-4 w-4 text-blue-600"
                />
                Set as default
              </label>
            </div>

            <div className="p-5 pt-0">
              <button
                onClick={handleUpdateCard}
                className="w-full bg-blue-600 text-white py-3 rounded-lg hover:bg-blue-700 transition-colors font-medium"
              >
                Save Changes
              </button>
            </div>
          </div>
        </div>
      )}

      {walletModalProvider && activeWallet && (
        <div className="fixed inset-0 bg-black/60 z-[60] flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md">
            <div className="flex items-center justify-between p-5 border-b border-gray-200">
              <div>
                <h3 className="text-lg font-bold text-gray-900">
                  {walletModalMode === 'manage' ? `${activeWallet.name} Wallet` : `Link ${activeWallet.name}`}
                </h3>
                <p className="text-sm text-gray-600">{activeWallet.description}</p>
              </div>
              <button
                onClick={closeWalletModal}
                className="w-10 h-10 rounded-full bg-gray-100 flex items-center justify-center hover:bg-gray-200"
              >
                <X className="w-5 h-5 text-gray-600" />
              </button>
            </div>

            <div className="p-5 space-y-4">
              {walletModalMode === 'link' && (
                <>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Mobile Number</label>
                    <input
                      type="tel"
                      value={walletMobile}
                      onChange={(e) => setWalletMobile(e.target.value.replace(/\D/g, '').slice(0, 10))}
                      placeholder="10-digit mobile number"
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>

                  <div className="space-y-3">
                    <label className="flex items-center justify-between text-sm text-gray-700">
                      <span>Device Verification</span>
                      <input
                        type="checkbox"
                        checked={walletDeviceVerified}
                        onChange={(e) => setWalletDeviceVerified(e.target.checked)}
                        className="h-4 w-4 text-blue-600"
                      />
                    </label>
                    <label className="flex items-center justify-between text-sm text-gray-700">
                      <span>Payment PIN Required</span>
                      <input
                        type="checkbox"
                        checked={walletPinRequired}
                        onChange={(e) => setWalletPinRequired(e.target.checked)}
                        className="h-4 w-4 text-blue-600"
                      />
                    </label>
                  </div>

                  {walletLinkError && <p className="text-xs text-red-600">{walletLinkError}</p>}
                  {walletOtpMessage && <p className="text-xs text-green-700">{walletOtpMessage}</p>}

                  <button
                    onClick={handleSendWalletOtp}
                    className="w-full bg-blue-600 text-white py-3 rounded-lg hover:bg-blue-700 transition-colors font-medium"
                  >
                    Send OTP
                  </button>
                </>
              )}

              {walletModalMode === 'otp' && (
                <>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">OTP</label>
                    <input
                      type="text"
                      value={walletOtp}
                      onChange={(e) => setWalletOtp(e.target.value.replace(/\D/g, '').slice(0, 6))}
                      placeholder="6-digit OTP"
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>

                  {walletLinkError && <p className="text-xs text-red-600">{walletLinkError}</p>}
                  {walletOtpMessage && <p className="text-xs text-green-700">{walletOtpMessage}</p>}

                  <button
                    onClick={handleVerifyWalletOtp}
                    className="w-full bg-green-600 text-white py-3 rounded-lg hover:bg-green-700 transition-colors font-medium"
                  >
                    Verify OTP
                  </button>
                </>
              )}

              {walletModalMode === 'manage' && (
                <>
                  <div className="space-y-2 text-sm text-gray-700">
                    <div className="flex items-center justify-between">
                      <span>Status</span>
                      <span className="font-semibold text-green-700">Linked</span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span>Mobile</span>
                      <span className="font-semibold">{activeWalletState?.mobile || 'N/A'}</span>
                    </div>
                    {typeof activeWalletState?.balance === 'number' && (
                      <div className="flex items-center justify-between">
                        <span>Balance</span>
                        <span className="font-semibold">{formatMoney(activeWalletState.balance)}</span>
                      </div>
                    )}
                  </div>

                  <label className="flex items-center justify-between text-sm text-gray-700">
                    <span>Use as default payment</span>
                    <input
                      type="checkbox"
                      checked={Boolean(activeWalletState?.isDefault)}
                      onChange={(e) => {
                        if (e.target.checked) {
                          handleSetDefaultWallet(walletModalProvider);
                        } else {
                          setLinkedPayments((prev) => ({
                            ...prev,
                            [walletModalProvider]: { ...prev[walletModalProvider], isDefault: false },
                          }));
                        }
                      }}
                      className="h-4 w-4 text-blue-600"
                    />
                  </label>

                  <label className="flex items-center justify-between text-sm text-gray-700">
                    <span>Auto Pay (Subscriptions)</span>
                    <input
                      type="checkbox"
                      checked={Boolean(activeWalletState?.autoPay)}
                      onChange={(e) => handleToggleAutoPay(walletModalProvider, e.target.checked)}
                      className="h-4 w-4 text-blue-600"
                    />
                  </label>

                  <button
                    onClick={() => handleUnlinkWallet(walletModalProvider)}
                    className="w-full border border-red-300 text-red-600 py-3 rounded-lg hover:bg-red-50 transition-colors font-medium"
                  >
                    Unlink Wallet
                  </button>
                </>
              )}
            </div>
          </div>
        </div>
      )}

      <MapAddressSelector
        isOpen={showMapSelector}
        onClose={() => setShowMapSelector(false)}
        onSelectAddress={handleMapAddressSelect}
        initialLocation={mapInitialLocation}
        existingLabels={savedAddresses.map((item) => item.type)}
        autoSelectInitial={false}
      />

      {showChangePasswordModal && (
        <div className="fixed inset-0 bg-black/60 z-[60] flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md">
            <div className="flex items-center justify-between p-5 border-b border-gray-200">
              <div>
                <h3 className="text-lg font-bold text-gray-900">Change Password</h3>
                <p className="text-sm text-gray-600">Update your account password</p>
              </div>
              <button
                onClick={() => setShowChangePasswordModal(false)}
                className="w-10 h-10 rounded-full bg-gray-100 flex items-center justify-center hover:bg-gray-200"
              >
                <X className="w-5 h-5 text-gray-600" />
              </button>
            </div>
            <div className="p-5 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Current Password</label>
                <div className="relative">
                  <input
                    type={changePasswordForm.showCurrent ? 'text' : 'password'}
                    value={changePasswordForm.currentPassword}
                    onChange={(e) =>
                      setChangePasswordForm((prev) => ({ ...prev, currentPassword: e.target.value }))
                    }
                    placeholder="Enter current password"
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 pr-12"
                  />
                  <button
                    type="button"
                    onClick={() =>
                      setChangePasswordForm((prev) => ({ ...prev, showCurrent: !prev.showCurrent }))
                    }
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                  >
                    {changePasswordForm.showCurrent ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                  </button>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">New Password</label>
                <div className="relative">
                  <input
                    type={changePasswordForm.showNew ? 'text' : 'password'}
                    value={changePasswordForm.newPassword}
                    onChange={(e) =>
                      setChangePasswordForm((prev) => ({ ...prev, newPassword: e.target.value }))
                    }
                    placeholder="Enter new password"
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 pr-12"
                  />
                  <button
                    type="button"
                    onClick={() =>
                      setChangePasswordForm((prev) => ({ ...prev, showNew: !prev.showNew }))
                    }
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                  >
                    {changePasswordForm.showNew ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                  </button>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Confirm New Password</label>
                <div className="relative">
                  <input
                    type={changePasswordForm.showConfirm ? 'text' : 'password'}
                    value={changePasswordForm.confirmPassword}
                    onChange={(e) =>
                      setChangePasswordForm((prev) => ({ ...prev, confirmPassword: e.target.value }))
                    }
                    placeholder="Re-enter new password"
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 pr-12"
                  />
                  <button
                    type="button"
                    onClick={() =>
                      setChangePasswordForm((prev) => ({ ...prev, showConfirm: !prev.showConfirm }))
                    }
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                  >
                    {changePasswordForm.showConfirm ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                  </button>
                </div>
              </div>

              {changePasswordError && <p className="text-sm text-red-600">{changePasswordError}</p>}
              {changePasswordSuccess && <p className="text-sm text-green-600">{changePasswordSuccess}</p>}
            </div>
            <div className="p-5 pt-0">
              <button
                onClick={handleChangePassword}
                disabled={changePasswordLoading}
                className="w-full bg-blue-600 text-white py-3 rounded-lg hover:bg-blue-700 transition-colors font-medium disabled:opacity-60"
              >
                {changePasswordLoading ? 'Updating...' : 'Update Password'}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}



